import SourceDocumentLinks from './SourceDocumentLinks';
import { flightSide, flightDayDifference, withFlightProgress } from '../../utils/flightDisplay';
import FlightDataSummary from './FlightDataSummary';
import { InlineAssignment } from '../../services/assignmentPreview';
import useDashboardInbox from '../../hooks/useDashboardInbox';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import TripLink from '../common/TripLink';
import { tripOriginState } from '../../utils/tripBackTarget';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { listUnfilledComponents, assignUnfilledComponent, deleteUnfilledComponent, fileUnfilledComponent } from '../../services/emailAddressService';
import { listStickyNotesForTrips, createStickyNote, createExecStickyNote, updateStickyNote, deleteStickyNote } from '../../services/stickyNoteService';
import { useAuth } from '../../contexts/AuthContext';
import { useParadigm } from '../../contexts/ParadigmContext';
import { useUnfiledComponentEvents, useStickyNoteEvents, useComponentEvents, useFlightUpdateEvents, SSEEventType } from '../../contexts/SSEContext';
import { formatWeekdayDate, formatTime, formatTimePreserveTimezone, formatDateTimeWithTimezone, formatWeekdayDatePreserveTimezone, formatDate, formatDateTime, formatDateTimePreserveTimezone } from '../../utils/dateTimeUtils';
import { getComponentStartDatetime, passesTimeFilter, componentAssignedToExecutive } from '../../utils/timelineFilters';
import { getAirlineByCode, getAirlineByName, parseFlightNumber, extractFlightNumber } from '../../utils/airlineUtils';
import {
  User,
  Plane,
  Building,
  Car,
  Utensils,
  Users,
  Calendar,
  MapPin,
  Clock,
  Hash,
  Loader2,
  Inbox,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  Briefcase,
  ArrowRight,
  Trash2,
  MoreVertical,
  RefreshCw,
  StickyNote as StickyNoteIcon,
  Plus,
  Lock,
  LockOpen,
  ChevronUp,
  Edit2,
  TrendingUp,
  Globe,
  Zap,
  Shield,
  Bell,
  Search,
  Settings,
  ArrowUpRight,
  CheckCircle2,
  AlertCircle,
  Info,
  X,
  List,
  LayoutGrid,
  FileText,
  Filter,
  AlertTriangle,
  Radio,
  Eye,
  EyeOff,
  Check,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarUI } from '../ui/calendar';
import StickyNoteModal from './StickyNoteModal';
import NotificationBell from './NotificationBell';
import StickyNote from './StickyNote';
import FlightStatusBadge, { FlightDelayIndicator } from './FlightStatusBadge';
import ParsingFeedbackModal from './ParsingFeedbackModal';
import CreateSpeakerProgramModal from './CreateSpeakerProgramModal';
import SpeakerProfileSelector from './SpeakerProfileSelector';
import CreateTripModal2 from './CreateTripModal2';
import EditTripModal2 from './EditTripModal2';
import ConfidentialitySettingsModal from './ConfidentialitySettingsModal';
import { PassengerNameList, formatPassengerName } from './PassengerNameList';
import { FlightOperationalInfo } from './TimelineCardVisuals';
import FareTermsIcons, { resolveFareTerms } from './FareTermsIcons';
import { groupTimelineByDay, consolidateJourneys, DayHeader, JourneyCard, AtAGlanceRail } from './ExecJourneyTimeline';
import { HotelStayCard, HotelCheckoutCard, findHotelTransfer, describeStop, hotelTransferEnds, URBANRIDE_ELIGIBLE_TYPES } from './HotelStayCards';

// ============================================================================
// COMPONENT TYPE STYLE CONFIGURATIONS
// ============================================================================

// Component type config (shared ui-* tokens)
const componentTypeConfigModern = {
  hotel: {
    gradient: 'from-ui-indigo-strong to-ui-violet-strong',
    bgLight: 'bg-ui-indigo-bg',
    border: 'border-ui-indigo-line',
    text: 'text-ui-indigo-fg',
    icon: Building
  },
  meeting: {
    gradient: 'from-ui-green-strong to-ui-teal-strong',
    bgLight: 'bg-ui-green-bg',
    border: 'border-ui-green-line',
    text: 'text-ui-green-fg',
    icon: Users
  },
  restaurant: {
    gradient: 'from-ui-orange-strong to-ui-amber-strong',
    bgLight: 'bg-ui-orange-bg',
    border: 'border-ui-orange-line',
    text: 'text-ui-orange-fg',
    icon: Utensils
  },
  private_flight: {
    gradient: 'from-ui-violet-strong to-ui-indigo-strong',
    bgLight: 'bg-ui-violet-bg',
    border: 'border-ui-violet-line',
    text: 'text-ui-violet-fg',
    icon: Plane
  },
  commercial_flight: {
    gradient: 'from-ui-primary to-ui-teal-strong',
    bgLight: 'bg-ui-blue-bg',
    border: 'border-ui-blue-line',
    text: 'text-ui-blue-fg',
    icon: Plane
  },
  flight: {
    gradient: 'from-ui-primary to-ui-teal-strong',
    bgLight: 'bg-ui-blue-bg',
    border: 'border-ui-blue-line',
    text: 'text-ui-blue-fg',
    icon: Plane
  },
  rental_car: {
    gradient: 'from-ui-green-strong to-ui-teal-strong',
    bgLight: 'bg-ui-green-bg',
    border: 'border-ui-green-line',
    text: 'text-ui-green-fg',
    icon: Car
  },
  car_rental: {
    gradient: 'from-ui-green-strong to-ui-teal-strong',
    bgLight: 'bg-ui-green-bg',
    border: 'border-ui-green-line',
    text: 'text-ui-green-fg',
    icon: Car
  },
  event: {
    gradient: 'from-ui-pink-strong to-ui-red-strong',
    bgLight: 'bg-ui-pink-bg',
    border: 'border-ui-pink-line',
    text: 'text-ui-pink-fg',
    icon: Calendar
  },
  chauffeur_service: {
    gradient: 'from-ui-muted to-ui-faint',
    bgLight: 'bg-ui-hover',
    border: 'border-ui-line',
    text: 'text-ui-ink-3',
    icon: Car
  },
  general: {
    gradient: 'from-ui-muted to-ui-faint',
    bgLight: 'bg-ui-hover',
    border: 'border-ui-line',
    text: 'text-ui-ink-3',
    icon: Calendar
  }
};

// Airport code to name mapping for common airports
const AIRPORT_NAMES = {
  'TLV': 'Tel Aviv', 'DXB': 'Dubai', 'CDG': 'Paris CDG', 'JFK': 'New York JFK',
  'LAX': 'Los Angeles', 'LHR': 'London Heathrow', 'FRA': 'Frankfurt', 'AMS': 'Amsterdam',
  'ORD': 'Chicago', 'ATL': 'Atlanta', 'SFO': 'San Francisco', 'LAS': 'Las Vegas',
  'MIA': 'Miami', 'BOS': 'Boston', 'SEA': 'Seattle', 'DEN': 'Denver',
  'PHX': 'Phoenix', 'DFW': 'Dallas', 'IAH': 'Houston', 'EWR': 'Newark',
  'LGA': 'LaGuardia', 'BWI': 'Baltimore', 'DCA': 'Washington', 'IAD': 'Dulles'
};

// Available component types for filter
const COMPONENT_TYPE_OPTIONS = [
  { value: 'commercial_flight', label: 'Flight' },
  { value: 'private_flight', label: 'Private Flight' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'rental_car', label: 'Car Rental' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'event', label: 'Event' },
  { value: 'general', label: 'General' }
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Parse datetime string without timezone conversion for consistent sorting
const parseDatetimeForSortValue = (dateStr) => {
  if (!dateStr) return null;
  try {
    const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})T?(\d{2})?:?(\d{2})?:?(\d{2})?/);
    if (!match) return null;
    const [, year, month, day, hour = '00', minute = '00', second = '00'] = match;
    return Date.UTC(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10),
      parseInt(second, 10)
    );
  } catch (e) {
    return null;
  }
};

// Get component start datetime
// getComponentStartDatetime now lives in utils/timelineFilters (shared with
// the trip timeline so filtering semantics stay in sync between the views).

// Get hotel full location
const getHotelFullLocation = (hotelData, fallbackLocation) => {
  if (hotelData?.hotel_address?.trim()) return hotelData.hotel_address;
  if (hotelData?.address?.trim()) return hotelData.address;
  if (hotelData?.location?.trim()) return hotelData.location;
  return fallbackLocation || '';
};

// Passenger name normalization + one-line "+N" list are shared with the trip
// timeline cards (see PassengerNameList.js) to keep both views consistent.

// Format date for display
const formatDateDisplay = (dateStr, prefs) => {
  if (!dateStr) return null;
  try {
    return formatDate(new Date(dateStr), prefs);
  } catch {
    return dateStr;
  }
};

// Format short date (e.g., "Dec 23")
const formatShortDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Check if two dates are on different days
const isDifferentDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toDateString() !== d2.toDateString();
  } catch {
    return false;
  }
};

// Get icon for component type
const getTypeIcon = (type, className = "w-5 h-5") => {
  switch (type) {
    case 'commercial_flight':
    case 'private_flight':
    case 'flight':
      return <Plane className={className} />;
    case 'hotel':
      return <Building className={className} />;
    case 'rental_car':
    case 'car_rental':
      return <Car className={className} />;
    case 'restaurant':
      return <Utensils className={className} />;
    case 'meeting':
      return <Users className={className} />;
    case 'event':
      return <Calendar className={className} />;
    default:
      return <Calendar className={className} />;
  }
};

// Get display name for component type
const getTypeName = (type) => {
  const names = {
    'commercial_flight': 'Flight',
    'private_flight': 'Private Flight',
    'hotel': 'Hotel',
    'rental_car': 'Car Rental',
    'car_rental': 'Car Rental',
    'restaurant': 'Restaurant',
    'meeting': 'Meeting',
    'event': 'Event'
  };
  return names[type] || type?.replace(/_/g, ' ') || 'Component';
};

// ============================================================================
// SHARED STYLE UTILITIES
// ============================================================================

/**
 * Returns shared classes for main containers
 */
const getContainerClasses = () => ({
  page: 'min-h-screen bg-ui-page',
  header: 'sticky top-0 z-30 relative overflow-hidden bg-ui-page',
  card: 'bg-ui-surface rounded-2xl border border-ui-line shadow-ui-card',
  cardHover: 'hover:border-ui-control hover:shadow-ui-pop',
  section: 'px-4 sm:px-6 lg:px-8 py-6 sm:py-8',
});

/**
 * Returns shared text classes
 */
const getTextClasses = () => ({
  title: 'text-ui-ink',
  subtitle: 'text-ui-ink-3',
  muted: 'text-ui-muted',
  accent: 'text-ui-link',
});

/**
 * Returns shared button classes
 */
const getButtonClasses = () => ({
  primary: 'bg-gradient-to-r from-ui-primary to-ui-teal-strong hover:opacity-90 text-ui-on-primary',
  secondary: 'bg-ui-hover border border-ui-control text-ui-ink-2 hover:bg-ui-raised',
  filter: 'bg-ui-hover backdrop-blur-sm border border-ui-line rounded-xl',
  filterActive: 'bg-ui-blue-bg text-ui-blue-fg shadow-lg',
  filterInactive: 'text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover',
});

/**
 * Returns shared input classes
 */
const getInputClasses = () => ({
  select: 'bg-ui-hover backdrop-blur-sm border border-ui-control rounded-lg text-ui-ink focus:ring-2 focus:ring-ui-primary/50 focus:border-ui-primary/50',
});

// ============================================================================
// COMPONENT: AirlineDisplay
// ============================================================================

const AirlineDisplay = ({ airlineCode, showCode = false }) => {
  const [airlineName, setAirlineName] = useState(null);
  const [resolvedCode, setResolvedCode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAirline = async () => {
      if (!airlineCode) {
        setLoading(false);
        return;
      }

      const input = String(airlineCode).trim();

      try {
        const isLikelyCode = input.length <= 3 && /^[A-Z0-9]+$/i.test(input);

        if (isLikelyCode) {
          const airline = await getAirlineByCode(input);
          if (airline) {
            setAirlineName(airline.name);
            setResolvedCode(input.toUpperCase());
          } else {
            setResolvedCode(input.toUpperCase());
          }
        } else {
          const airline = await getAirlineByName(input);
          if (airline) {
            setAirlineName(airline.name || input);
            setResolvedCode(airline.code ? airline.code.toUpperCase() : null);
          } else {
            setAirlineName(input);
          }
        }
      } catch (error) {
        const isLikelyCode = input.length <= 3 && /^[A-Z0-9]+$/i.test(input);
        if (isLikelyCode) {
          setResolvedCode(input.toUpperCase());
        } else {
          setAirlineName(input);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAirline();
  }, [airlineCode]);

  if (loading) return <span>Loading...</span>;
  if (!airlineCode) return <span>—</span>;

  if (showCode && airlineName && resolvedCode) {
    return <span>{airlineName} ({resolvedCode})</span>;
  }
  if (airlineName) return <span>{airlineName}</span>;
  if (resolvedCode) return <span>{resolvedCode}</span>;
  return <span>{airlineCode}</span>;
};

// ============================================================================
// COMPONENT: MultiSelectDropdown
// ============================================================================

const MultiSelectDropdown = ({
  label,
  options,
  selectedValues,
  onChange,
  icon: Icon,
  allSelectedLabel = 'All'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allSelected = selectedValues.length === options.length;
  const noneSelected = selectedValues.length === 0;

  const handleToggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.value));
    }
  };

  const handleToggleOption = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const getDisplayText = () => {
    if (allSelected || noneSelected) return allSelectedLabel;
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option?.label || selectedValues[0];
    }
    return `${selectedValues.length} selected`;
  };

  const buttonClasses = `flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg transition-colors ${
    !allSelected && !noneSelected
      ? 'bg-ui-blue-bg border-ui-blue-line text-ui-blue-fg'
      : 'bg-ui-surface border-ui-control text-ui-ink-2 hover:border-ui-blue-line'
  }`;

  // Anchor left on mobile (right-anchoring to a narrow trigger near the left
  // viewport edge pushes the 14rem panel off-screen); right on sm+ as before.
  const dropdownClasses = 'absolute left-0 sm:left-auto sm:right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] bg-ui-raised border border-ui-control rounded-xl shadow-ui-pop z-50';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClasses}
      >
        {Icon && <Icon className="w-4 h-4" />}
        <span className="max-w-[100px] truncate">{getDisplayText()}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={dropdownClasses}>
          <div className="p-2">
            <button
              onClick={handleToggleAll}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors text-ui-ink-2 hover:bg-ui-hover"
            >
              <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
              {allSelected && <Check className="w-4 h-4 text-ui-blue-fg" />}
            </button>
          </div>
          <div className="border-t border-ui-divider" />
          <div className="p-2 max-h-60 overflow-y-auto">
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => handleToggleOption(option.value)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                    isSelected
                      ? 'bg-ui-blue-bg text-ui-blue-fg'
                      : 'text-ui-ink-3 hover:bg-ui-hover hover:text-ui-ink'
                  }`}
                >
                  <span>{option.label}</span>
                  {isSelected && <Check className="w-4 h-4 text-ui-blue-fg" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENT: FlightCard (Theme-Aware) - Matches original ExecDashboard2 design
// ============================================================================

const FlightCard = ({ component, onViewDetails, stickyNotes = [], currentUserId, onToggleNoteExpand, onEditNote, onDeleteNote, onNotePositionChange, onNoteMoveToComponent, onReportFeedback, prefs, selectedExecutiveId, executives, onOpenConfidentiality, isExecutiveUser = false, tripAccess = 'write' }) => {
  const canViewTrip = !isExecutiveUser || tripAccess === 'read' || tripAccess === 'write';
  const canEditTrip = !isExecutiveUser || tripAccess === 'write';
  const cardRef = useRef(null);
  // Status and delays checked against the recorded times (see flightProgress).
  const leg = withFlightProgress(component.flightLeg || {});
  const flightId = component.originalComponent?.component_id || component.component_id;
  const configModern = componentTypeConfigModern[component.component_type] || componentTypeConfigModern.general;
  const isPrivate = component.isPrivateFlight || component.component_type === 'private_flight';

  // For private flights: prefer ICAO (4-letter), for commercial: use IATA (3-letter)
  const depCode = isPrivate
    ? (leg.departure_airport_icao || leg.departure_airport_iata || leg.departure_airport || '')
    : (leg.departure_airport || leg.departure_airport_code || '');
  const arrCode = isPrivate
    ? (leg.arrival_airport_icao || leg.arrival_airport_iata || leg.arrival_airport || '')
    : (leg.arrival_airport || leg.arrival_airport_code || '');

  const depName = isPrivate
    ? (leg.departure_airport_name || AIRPORT_NAMES[depCode] || '')
    : (AIRPORT_NAMES[depCode] || depCode);
  const arrName = isPrivate
    ? (leg.arrival_airport_name || AIRPORT_NAMES[arrCode] || '')
    : (AIRPORT_NAMES[arrCode] || arrCode);

  // FBO info for private flights
  const depFboName = leg.departure_fbo_name || '';
  const depFboAddress = leg.departure_fbo_address || '';
  const arrFboName = leg.arrival_fbo_name || '';
  const arrFboAddress = leg.arrival_fbo_address || '';
  const aircraftType = leg.aircraft_type
    || component.component_data?.aircraft_type
    || component.originalComponent?.component_data?.aircraft_type
    || '';
  const hasOperationalInfo = !isPrivate || Boolean(aircraftType);

  const depDisplayTime = flightSide(leg, 'departure', prefs, component.start_datetime);
  const arrDisplayTime = flightSide(leg, 'arrival', prefs, component.end_datetime);
  const updatedDepDateTime = depDisplayTime.raw;
  const updatedArrDateTime = arrDisplayTime.raw;
  const hasUpdatedDep = depDisplayTime.kind !== 'Scheduled';
  const hasUpdatedArr = arrDisplayTime.kind !== 'Scheduled';
  const depTime = depDisplayTime.time, arrTime = arrDisplayTime.time;
  const scheduledDepTime = hasUpdatedDep ? depDisplayTime.scheduled.time : null;
  const scheduledArrTime = hasUpdatedArr ? arrDisplayTime.scheduled.time : null;
  const depDate = depDisplayTime.date, arrDate = arrDisplayTime.date;
  const dayDifference = flightDayDifference(depDisplayTime, arrDisplayTime);
  const isOvernightFlight = dayDifference !== 0;

  let airlineCode = leg.airline_code || leg.iata_code || '';
  if (!airlineCode && leg.flight_number) {
    const match = leg.flight_number.match(/^([A-Z]{2})/i);
    if (match) airlineCode = match[1].toUpperCase();
  }

  const flightNumber = leg.flight_number || '';
  const flightNumDisplay = airlineCode && flightNumber.toUpperCase().startsWith(airlineCode.toUpperCase())
    ? flightNumber.substring(airlineCode.length).trim()
    : flightNumber;

  // Tail number for private flights
  const tailNumber = leg.tail_number || component.component_data?.tail_number || '';

  const hasDelay = leg.delay_minutes != null;
  const isDiverted = leg.diverted || (leg.flight_status || '').toLowerCase() === 'diverted';
  const hasActiveAlert = leg.has_active_alert;

  const getEffectiveStatus = () => {
    if (leg.flight_status) return leg.flight_status;
    const now = new Date();
    const arrivalTime = updatedArrDateTime ? new Date(updatedArrDateTime) : null;
    const departureTime = updatedDepDateTime ? new Date(updatedDepDateTime) : null;
    if (arrivalTime && arrivalTime < now) return null;
    if (departureTime && departureTime < now) return null;
    return 'scheduled';
  };

  const effectiveStatus = getEffectiveStatus();

  // Get passenger names for display
  const getPassengerDisplayInfo = () => {
    // Collect passengers from various sources
    const cd = component.component_data || component.originalComponent?.component_data || {};
    let allPassengers = [];

    // Check component-level passengers first
    if (cd.passengers && cd.passengers.length > 0) {
      cd.passengers.forEach(p => {
        const rawName = typeof p === 'string' ? p : (p.name || p.passenger_name || `${p.first_name || ''} ${p.last_name || ''}`.trim());
        const formattedName = formatPassengerName(rawName);
        if (formattedName && !allPassengers.includes(formattedName)) {
          allPassengers.push(formattedName);
        }
      });
    }

    // Check leg-level passengers (seat assignments, etc.)
    const legPassengers = leg.leg_passengers || leg.passengers || leg.seat_assignments || [];
    legPassengers.forEach(p => {
      const rawName = typeof p === 'string' ? p : (p.passenger_name || p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim());
      const formattedName = formatPassengerName(rawName);
      if (formattedName && !allPassengers.includes(formattedName)) {
        allPassengers.push(formattedName);
      }
    });

    // Check for single traveler fields
    const singleTravelerName = cd.traveler_name || cd.passenger_name || cd.guest_name;
    if (singleTravelerName) {
      const formattedName = formatPassengerName(singleTravelerName);
      if (formattedName && !allPassengers.includes(formattedName)) {
        allPassengers.push(formattedName);
      }
    }

    if (allPassengers.length === 0) return { primaryName: null, additionalNames: [] };

    // If a selectedExecutiveId is provided, try to match and prioritize that executive
    let primaryName = allPassengers[0];
    let additionalNames = allPassengers.slice(1);

    if (selectedExecutiveId && executives && executives.length > 0) {
      const selectedExec = executives.find(e => e.id === selectedExecutiveId);
      if (selectedExec) {
        const execName = selectedExec.full_name || `${selectedExec.first_name || ''} ${selectedExec.last_name || ''}`.trim();
        // Find if any passenger name matches the selected executive
        const matchIndex = allPassengers.findIndex(pName => {
          const pNameLower = pName.toLowerCase();
          const execNameLower = execName.toLowerCase();
          const firstNameLower = (selectedExec.first_name || '').toLowerCase();
          const lastNameLower = (selectedExec.last_name || '').toLowerCase();
          // Check for name matches (could be partial matches)
          return pNameLower === execNameLower ||
                 pNameLower.includes(firstNameLower) && pNameLower.includes(lastNameLower) ||
                 execNameLower.includes(pNameLower.split(' ')[0]) && execNameLower.includes(pNameLower.split(' ').slice(-1)[0]);
        });
        if (matchIndex >= 0) {
          primaryName = allPassengers[matchIndex];
          additionalNames = allPassengers.filter((_, i) => i !== matchIndex);
        }
      }
    }

    return { primaryName, additionalNames };
  };

  const { primaryName: passengerPrimaryName, additionalNames: passengerAdditionalNames } = getPassengerDisplayInfo();

  // Per-passenger booking details for the current leg (confirmation, ticket, seat)
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const getLegBookingDetails = () => {
    const cd = component.component_data || component.originalComponent?.component_data || {};
    const passengers = Array.isArray(cd.passengers) ? cd.passengers : [];
    const seatAssignments = Array.isArray(leg.seat_assignments) ? leg.seat_assignments : [];
    const compConfirmation = cd.confirmation_number || component.confirmation_number || null;
    const compTicket = cd.ticket_number || null;

    if (passengers.length === 0) {
      const row = {
        name: null,
        confirmation: compConfirmation,
        ticket: compTicket,
        seat: leg.seat || null
      };
      return (row.confirmation || row.ticket || row.seat) ? [row] : [];
    }

    return passengers.map((p, idx) => {
      const isObj = typeof p === 'object' && p !== null;
      const rawName = isObj
        ? (p.name || p.passenger_name || `${p.first_name || ''} ${p.last_name || ''}`.trim())
        : p;
      const name = formatPassengerName(rawName) || `Passenger ${idx + 1}`;
      const confirmation = (isObj && p.confirmation_number) || compConfirmation || null;
      const ticket = (isObj && (p.ticket_number || p.ticket_no || p.document_number || p.e_ticket_number)) || compTicket || null;

      let seat = null;
      const sa = seatAssignments.find(s => s.passenger_name === rawName || s.passenger_index === idx);
      if (sa?.seat) seat = sa.seat;
      else if (leg.seat && passengers.length === 1) seat = leg.seat;
      else if (isObj) seat = p.seat || p.seat_number || null;

      return { name, confirmation, ticket, seat };
    });
  };

  const legBookingDetails = !isPrivate ? getLegBookingDetails() : [];
  const hasLegBookingInfo = legBookingDetails.some(d => d.confirmation || d.ticket || d.seat);

  // Matches original ExecDashboard2 FlightCard
  return (
    <div
      ref={cardRef}
      className="group relative bg-ui-surface rounded-2xl border border-ui-line p-4 sm:p-5 hover:border-ui-control transition-all duration-300 hover:shadow-ui-pop min-h-[180px]"
      data-component-id={component.id}
      data-trip-id={component.tripId}
    >
      {/* Floating sticky notes */}
      {stickyNotes && stickyNotes.length > 0 && stickyNotes.map(note => (
        <div key={note.id} className="absolute z-20 pointer-events-auto" style={{ left: `${note.position?.offset_x || 5}%`, top: `${note.position?.offset_y || 5}%` }}>
          <StickyNote note={note} currentUserId={currentUserId} onToggleExpand={onToggleNoteExpand} onEdit={onEditNote} onDelete={onDeleteNote} isDraggable={true} isFloating={true} containerRef={cardRef} currentComponentId={component.id} onPositionChange={onNotePositionChange} onMoveToComponent={onNoteMoveToComponent} />
        </div>
      ))}

      {/* Date header with status badge */}
      {depDate && (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-4 pb-3 border-b border-ui-divider">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 min-w-0">
            <Calendar className="w-4 h-4 text-ui-link flex-shrink-0" />
            <span className="text-sm font-medium text-ui-ink whitespace-nowrap">{depDate}</span>
            {/* Confidential badge */}
            {component.isConfidential ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenConfidentiality) {
                    onOpenConfidentiality({
                      isUnfiled: false,
                      tripId: component.tripId,
                      componentId: component.originalId || component.id,
                      title: component.title || `${component.component_type?.replace(/_/g, ' ')} Component`
                    });
                  }
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-ui-amber-bg text-ui-amber-fg border border-ui-amber-line hover:bg-ui-amber-line transition-colors cursor-pointer"
                title="Click to manage confidentiality settings"
              >
                <Lock className="w-3 h-3" />
                <span>Confidential</span>
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenConfidentiality) {
                    onOpenConfidentiality({
                      isUnfiled: false,
                      tripId: component.tripId,
                      componentId: component.originalId || component.id,
                      title: component.title || `${component.component_type?.replace(/_/g, ' ')} Component`
                    });
                  }
                }}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-ui-hover text-ui-ink-3 border border-ui-control hover:bg-ui-line transition-colors cursor-pointer"
                title="Click to manage confidentiality settings"
              >
                <LockOpen className="w-3 h-3" />
              </button>
            )}
            <SourceDocumentLinks component={component} />
            {/* Active Alert Tracking Indicator */}
            {hasActiveAlert && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-ui-green-bg text-ui-green-fg border border-ui-green-line" title="Flight tracking active">
                <Radio className="w-3 h-3" />
                <span>Tracking</span>
              </span>
            )}
            {/* Status badge inline with date */}
            {effectiveStatus && (
              <FlightStatusBadge status={isDiverted ? 'diverted' : effectiveStatus} delayMinutes={leg.delay_minutes} size="small" variant="theme" />
            )}
          </div>
          {isOvernightFlight && arrDate && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-xs text-ui-muted">arrives</span>
              <span className="text-sm font-medium text-ui-amber-fg whitespace-nowrap">{arrDate}</span>
              <span className="text-xs px-1.5 py-0.5 bg-ui-amber-bg text-ui-amber-fg rounded font-medium">{dayDifference > 0 ? '+' : ''}{dayDifference}</span>
            </div>
          )}
        </div>
      )}

      {/* Flight route visualization */}
      <div className="flex items-center gap-2 sm:gap-4 mb-4">
        {/* Departure */}
        <div className="flex-1 min-w-0">
          <div className={`${isPrivate ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'} font-bold text-ui-ink tracking-tight`}>{depCode}</div>
          {isPrivate ? (
            <>
              {depName && <div className="text-xs sm:text-sm text-ui-ink-3 truncate">{depName}</div>}
              {depFboName && <div className="text-xs text-ui-amber-fg truncate" title={depFboAddress || depFboName}>{depFboName}</div>}
              {depFboAddress && !depFboName && <div className="text-xs text-ui-muted truncate">{depFboAddress}</div>}
            </>
          ) : (
            <div className="text-xs sm:text-sm text-ui-ink-3 truncate">{depName}</div>
          )}
          <div className="mt-1">
            <div className={`text-base sm:text-lg font-semibold ${hasUpdatedDep ? 'text-ui-green-fg' : 'text-ui-ink'}`}>{depTime || '--:--'}</div>
            {scheduledDepTime && scheduledDepTime !== depTime && <div className="text-xs text-ui-muted line-through">{scheduledDepTime}</div>}
          </div>
        </div>

        {/* Flight path indicator */}
        <div className="flex-shrink-0 w-16 sm:w-24 flex flex-col items-center">
          <div className="w-full flex items-center gap-1 sm:gap-2">
            <div className="h-px flex-1 bg-gradient-to-r from-ui-line to-ui-blue-strong" />
            <div className={`p-1.5 sm:p-2 rounded-full bg-gradient-to-br ${configModern.gradient}`}>
              <Plane className="w-3 h-3 sm:w-4 sm:h-4 text-ui-on-primary rotate-90" />
            </div>
            <div className="h-px flex-1 bg-gradient-to-r from-ui-blue-strong to-ui-line" />
          </div>
          {/* Show tail number for private flights, airline code + flight number for commercial */}
          {isPrivate ? (
            tailNumber && (
              <div className="flex items-center gap-1 mt-1 sm:mt-2">
                <span className="text-[10px] sm:text-xs font-bold text-ui-violet-fg bg-ui-violet-bg px-1 sm:px-1.5 py-0.5 rounded">{tailNumber}</span>
              </div>
            )
          ) : (
            (airlineCode || flightNumber) && (
              <div className="flex items-center gap-1 mt-1 sm:mt-2">
                {airlineCode && <span className="text-[10px] sm:text-xs font-bold text-ui-link bg-ui-blue-bg px-1 sm:px-1.5 py-0.5 rounded">{airlineCode}</span>}
                {flightNumDisplay && <span className="text-[10px] sm:text-xs text-ui-muted font-medium truncate max-w-[40px] sm:max-w-none">{flightNumDisplay}</span>}
              </div>
            )
          )}
        </div>

        {/* Arrival */}
        <div className="flex-1 min-w-0 text-right">
          <div className={`${isPrivate ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'} font-bold text-ui-ink tracking-tight`}>{arrCode}</div>
          {isPrivate ? (
            <>
              {arrName && <div className="text-xs sm:text-sm text-ui-ink-3 truncate">{arrName}</div>}
              {arrFboName && <div className="text-xs text-ui-amber-fg truncate" title={arrFboAddress || arrFboName}>{arrFboName}</div>}
              {arrFboAddress && !arrFboName && <div className="text-xs text-ui-muted truncate">{arrFboAddress}</div>}
            </>
          ) : (
            <div className="text-xs sm:text-sm text-ui-ink-3 truncate">{arrName}</div>
          )}
          <div className="mt-1">
            <div className={`text-base sm:text-lg font-semibold ${hasUpdatedArr ? 'text-ui-green-fg' : 'text-ui-ink'}`}>{arrTime || '--:--'}</div>
            {scheduledArrTime && scheduledArrTime !== arrTime && <div className="text-xs text-ui-muted line-through">{scheduledArrTime}</div>}
          </div>
        </div>
      </div>

      <FlightDataSummary leg={leg} prefs={prefs} flightId={isPrivate ? null : flightId} />
      <FlightOperationalInfo
        departureTerminal={leg.departure_terminal}
        departureGate={leg.departure_gate}
        arrivalTerminal={leg.arrival_terminal}
        arrivalGate={leg.arrival_gate}
        departureCheckIn={leg.departure_checkin}
        arrivalBaggageClaim={leg.arrival_baggage_claim}
        aircraftType={aircraftType}
        showAirportDetails={!isPrivate}
        trailing={<FareTermsIcons terms={resolveFareTerms(leg,
          component.component_data || component.originalComponent?.component_data)} />}
      />

      {/* Passenger names on left, Trip/Edit/Report on right */}
      <div className={`flex items-center justify-between pt-3 ${hasOperationalInfo ? '' : 'border-t border-ui-divider'}`}>
        {/* Passenger name on left side */}
        <div className="flex-1 min-w-0">
          {passengerPrimaryName && (
            <PassengerNameList
              names={[passengerPrimaryName, ...passengerAdditionalNames]}
              variant="modern"
              IconComponent={User}
            />
          )}
        </div>

        {/* Trip name, Edit, and Feedback buttons - right side */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {canViewTrip ? (
            <TripLink to={`/Trip/${component.tripId}`} className="text-xs text-ui-ink-3 hover:text-ui-link transition-colors truncate max-w-[100px]" title={component.tripTitle}>
              {component.tripTitle || 'Trip'}
            </TripLink>
          ) : (
            <span className="text-xs text-ui-muted truncate max-w-[100px]" title={component.tripTitle}>
              {component.tripTitle || 'Trip'}
            </span>
          )}
          {canEditTrip && (
            <TripLink to={`/Trip/${component.tripId}?editComponent=${component.originalId || component.id}`} className="text-xs text-ui-link hover:text-ui-blue-fg flex items-center gap-1 transition-colors">
              <Edit2 className="w-3 h-3" />
            </TripLink>
          )}
          {onReportFeedback && (
            <button onClick={() => onReportFeedback(component)} className="text-xs text-ui-amber-fg hover:text-ui-amber-fg flex items-center gap-1 transition-colors" title="Report parsing issue">
              <AlertTriangle className="w-3 h-3" />
            </button>
          )}
          {hasLegBookingInfo && (
            <button
              onClick={(e) => { e.stopPropagation(); setIsDetailsExpanded(v => !v); }}
              className="p-1 rounded-md text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover transition-colors"
              title={isDetailsExpanded ? 'Hide booking details' : 'Show booking details'}
              aria-expanded={isDetailsExpanded}
            >
              {isDetailsExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Expanded booking details (per-passenger for this leg) */}
      {hasLegBookingInfo && isDetailsExpanded && (
        <div className="mt-3 pt-3 border-t border-ui-divider space-y-3">
          {legBookingDetails.map((d, idx) => (
            <div key={idx} className={idx > 0 ? 'pt-3 border-t border-ui-divider' : ''}>
              {d.name && legBookingDetails.length > 1 && (
                <div className="text-xs font-semibold text-ui-ink mb-1.5 truncate">{d.name}</div>
              )}
              <dl className="grid grid-cols-[auto,1fr] gap-x-2 sm:gap-x-3 gap-y-1 text-xs">
                <dt className="text-ui-muted">Confirmation</dt>
                <dd className="font-medium text-ui-ink break-all">{d.confirmation || '—'}</dd>
                <dt className="text-ui-muted">Ticket</dt>
                <dd className="font-medium text-ui-ink break-all">{d.ticket || '—'}</dd>
                <dt className="text-ui-muted">Seat</dt>
                <dd className="font-medium text-ui-ink break-all">{d.seat || '—'}</dd>
              </dl>
            </div>
          ))}
        </div>
      )}

      {/* Delay indicator if present */}
      {hasDelay && (
        <div className="mt-3 pt-3 border-t border-ui-divider">
          <FlightDelayIndicator delayMinutes={leg.delay_minutes} variant="theme" />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENT: HotelCard (Theme-Aware) - Matches original ExecDashboard2 design
// ============================================================================

const HotelCard = ({ component, onViewDetails, stickyNotes = [], currentUserId, onToggleNoteExpand, onEditNote, onDeleteNote, onNotePositionChange, onNoteMoveToComponent, onReportFeedback, prefs, onOpenConfidentiality, isExecutiveUser = false, tripAccess = 'write' }) => {
  const canViewTrip = !isExecutiveUser || tripAccess === 'read' || tripAccess === 'write';
  const canEditTrip = !isExecutiveUser || tripAccess === 'write';
  const cardRef = useRef(null);
  const cd = component.component_data || component.hotelData || {};
  const hotelName = cd.hotel_name || component.title || 'Hotel';
  const location = getHotelFullLocation(cd, component.location);
  const isCheckIn = component.eventType === 'checkin';

  // Get guest names - check multiple possible data sources (normalized from airline format)
  const getGuestNames = () => {
    // Check component_data and hotelData - sometimes data is in one but not the other
    const sources = [cd, component.hotelData, component.component_data, component.originalComponent?.component_data].filter(Boolean);

    for (const source of sources) {
      // Try guests array first (may be objects with name field or plain strings)
      const guests = source.guests || source.passengers || [];
      if (guests.length > 0) {
        const names = guests.map(g => {
          const rawName = typeof g === 'string' ? g : (g.name || g.guest_name || `${g.first_name || ''} ${g.last_name || ''}`.trim());
          return formatPassengerName(rawName);
        }).filter(Boolean);
        if (names.length > 0) return names.join(', ');
      }
      // Check single name fields
      const singleName = source.guest_name || source.passenger_name || source.traveler_name;
      if (singleName) return formatPassengerName(singleName);
    }
    return null;
  };
  const guestNames = getGuestNames();

  // Get the appropriate datetime based on event type
  const eventDateTime = isCheckIn ? cd.check_in : cd.check_out;
  const dateDisplay = formatDateDisplay(eventDateTime || component.start_datetime, prefs);
  const timeDisplay = formatTimePreserveTimezone(eventDateTime || component.start_datetime, prefs);

  // Matches original ExecDashboard2 HotelCard
  return (
    <div
      ref={cardRef}
      className="group relative bg-ui-surface rounded-2xl border border-ui-line p-5 hover:border-ui-indigo-line transition-all duration-300 hover:shadow-ui-pop min-h-[140px]"
      data-component-id={component.id}
      data-trip-id={component.tripId}
    >
      {/* Floating sticky notes */}
      {stickyNotes && stickyNotes.length > 0 && stickyNotes.map(note => (
        <div key={note.id} className="absolute z-20 pointer-events-auto" style={{ left: `${note.position?.offset_x || 5}%`, top: `${note.position?.offset_y || 5}%` }}>
          <StickyNote note={note} currentUserId={currentUserId} onToggleExpand={onToggleNoteExpand} onEdit={onEditNote} onDelete={onDeleteNote} isDraggable={true} isFloating={true} containerRef={cardRef} currentComponentId={component.id} onPositionChange={onNotePositionChange} onMoveToComponent={onNoteMoveToComponent} />
        </div>
      ))}

      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl bg-gradient-to-br from-ui-indigo-strong to-ui-violet-strong shadow-lg shadow-ui-indigo-solid/30">
          <Building className="w-5 h-5 text-ui-on-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${isCheckIn ? 'bg-ui-green-bg text-ui-green-fg' : 'bg-ui-amber-bg text-ui-amber-fg'}`}>
              {isCheckIn ? 'Check-in' : 'Check-out'}
            </span>
            {component.isConfidential ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenConfidentiality) {
                    onOpenConfidentiality({
                      isUnfiled: false,
                      tripId: component.tripId,
                      componentId: component.originalId || component.id,
                      title: component.title || hotelName
                    });
                  }
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-ui-amber-bg text-ui-amber-fg border border-ui-amber-line hover:bg-ui-amber-line transition-colors cursor-pointer"
                title="Click to manage confidentiality settings"
              >
                <Lock className="w-3 h-3" />
                Confidential
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenConfidentiality) {
                    onOpenConfidentiality({
                      isUnfiled: false,
                      tripId: component.tripId,
                      componentId: component.originalId || component.id,
                      title: component.title || hotelName
                    });
                  }
                }}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-ui-hover text-ui-ink-3 border border-ui-control hover:bg-ui-line transition-colors cursor-pointer"
                title="Click to manage confidentiality settings"
              >
                <LockOpen className="w-3 h-3" />
              </button>
            )}
          </div>
          <h3 className="text-lg font-semibold text-ui-on-primary truncate">{hotelName}</h3>
          {location && (
            <div className="flex items-center gap-1 text-sm text-ui-ink-3 mt-1">
              <MapPin className="w-3.5 h-3.5" />
              <span className="truncate">{location}</span>
            </div>
          )}
          {guestNames && (
            <div className="flex items-center gap-1 text-sm text-ui-ink-3 mt-1">
              <User className="w-3.5 h-3.5" />
              <span className="truncate">{guestNames}</span>
            </div>
          )}
          <div className="flex items-center gap-1 text-sm text-ui-muted mt-2">
            <Clock className="w-3.5 h-3.5" />
            <span>{dateDisplay}{timeDisplay ? ` at ${timeDisplay}` : ''}</span><SourceDocumentLinks component={component} />
          </div>
          <div className="mt-2">
            {/* Tappable trip row: flex makes the link block-level so `truncate`
                actually clips (inline anchors can't hide overflow, which let
                long program titles escape the card) */}
            {canViewTrip ? (
              <TripLink
                to={`/Trip/${component.tripId}`}
                title={component.tripTitle}
                className="flex items-center gap-1.5 min-w-0 text-xs text-ui-ink-3 hover:text-ui-link transition-colors"
              >
                <Briefcase className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{component.tripTitle || 'Trip'}</span>
                <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
              </TripLink>
            ) : (
              <span className="flex items-center gap-1.5 min-w-0 text-xs text-ui-muted" title={component.tripTitle}>
                <Briefcase className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{component.tripTitle || 'Trip'}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onReportFeedback && (
            <button onClick={() => onReportFeedback(component)} className="p-2 text-ui-amber-fg hover:text-ui-amber-fg hover:bg-ui-hover rounded-lg transition-colors" title="Report parsing issue">
              <AlertTriangle className="w-4 h-4" />
            </button>
          )}
          {canEditTrip && (
            <TripLink to={`/Trip/${component.tripId}?editComponent=${component.originalId || component.id}`} className="p-2 text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover rounded-lg transition-colors" title="Edit">
              <Edit2 className="w-4 h-4" />
            </TripLink>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENT: GenericCard (Theme-Aware) - Matches original ExecDashboard2 ComponentCard
// ============================================================================

const GenericCard = ({ component, stickyNotes = [], currentUserId, onToggleNoteExpand, onEditNote, onDeleteNote, onNotePositionChange, onNoteMoveToComponent, onReportFeedback, prefs, selectedExecutiveId, executives, onOpenConfidentiality, isExecutiveUser = false, tripAccess = 'write' }) => {
  const canViewTrip = !isExecutiveUser || tripAccess === 'read' || tripAccess === 'write';
  const canEditTrip = !isExecutiveUser || tripAccess === 'write';
  const cardRef = useRef(null);
  const configModern = componentTypeConfigModern[component.component_type] || componentTypeConfigModern.general;
  const Icon = configModern.icon || Calendar;
  const cd = component.component_data || {};

  const getTitle = () => {
    if (component.title) return component.title;
    switch (component.component_type) {
      case 'restaurant': return cd.restaurant_name || cd.name || 'Restaurant';
      case 'meeting': return cd.meeting_name || 'Meeting';
      case 'rental_car':
      case 'car_rental': return cd.rental_company || 'Car Rental';
      default: return component.component_type?.replace(/_/g, ' ') || 'Event';
    }
  };

  // Get address for restaurants
  const getAddress = () => {
    if (component.component_type === 'restaurant') {
      return cd.address || cd.full_address || '';
    }
    return '';
  };

  const getLocation = () => {
    // For rental car events, use the location set on the component directly
    if (component.isRentalCarEvent && component.location) {
      return component.location;
    }

    // For chauffeur service with airport pickup and flight info, format as "JFK AA 225 (origin)"
    if (component.component_type === 'chauffeur_service') {
      const pickupLocation = cd.pickup_location || '';
      const flightInfo = cd.flight_info || '';

      // Check if this is an airport pickup with flight info
      if (flightInfo && pickupLocation) {
        // Try to extract airport code from pickup_location (e.g., "JFK Airport" -> "JFK")
        const airportMatch = pickupLocation.match(/\b([A-Z]{3})\b/);
        const pickupAirport = airportMatch ? airportMatch[1] : '';

        // Try to parse flight info for airline code, flight number, and origin
        // Common patterns: "AA 225", "AA225", "American Airlines 225", "AA 225 from LAX", "arriving from LAX on AA 225"
        const flightMatch = flightInfo.match(/\b([A-Z]{2})\s*(\d{1,4})\b/);
        const originMatch = flightInfo.match(/(?:from|arriving from|origin:?)\s*([A-Z]{3})\b/i);

        if (pickupAirport && flightMatch) {
          const airlineCode = flightMatch[1];
          const flightNumber = flightMatch[2];
          const origin = originMatch ? originMatch[1].toUpperCase() : '';

          if (origin) {
            return `${pickupAirport} ${airlineCode} ${flightNumber} (${origin})`;
          } else {
            return `${pickupAirport} ${airlineCode} ${flightNumber}`;
          }
        }
      }

      // Fall back to pickup_location
      return pickupLocation;
    }

    // For events, show venue name + address when both exist
    if (component.component_type === 'event') {
      const venueName = cd.event_venue_name || '';
      const address = cd.event_location || component.location || '';
      if (venueName && address) return `${venueName}, ${address}`;
      return venueName || address || component.location || '';
    }

    return cd.location || cd.meeting_location || cd.pickup_location || component.location || '';
  };

  // Get renter/driver name for rental cars
  const getRenterName = () => {
    const isCarRental = component.component_type === 'car_rental' || component.component_type === 'rental_car' || component.isRentalCarEvent;
    if (!isCarRental) return null;

    // Use rentalData for pickup/return events, otherwise use component_data
    const rentalData = component.rentalData || cd;
    const rawName = rentalData.driver_name || rentalData.traveler_name || rentalData.renter_name || rentalData.guest_name || rentalData.passenger_name;
    if (rawName) return formatPassengerName(rawName);

    // Also check passengers array
    const passengers = rentalData.passengers || [];
    if (passengers.length > 0) {
      const firstPassenger = passengers[0];
      const name = typeof firstPassenger === 'string' ? firstPassenger : (firstPassenger.name || firstPassenger.passenger_name || '');
      return formatPassengerName(name);
    }
    return null;
  };

  const renterName = getRenterName();

  // Get attendees for restaurants, meetings, and chauffeur services with primary/additional split
  const getAttendeesDisplayInfo = () => {
    const isRestaurant = component.component_type === 'restaurant';
    const isMeeting = component.component_type === 'meeting';
    const isChauffeur = component.component_type === 'chauffeur_service';
    if (!isRestaurant && !isMeeting && !isChauffeur) return { primaryName: null, additionalNames: [] };

    let allAttendees = [];

    // Get attendees/passengers based on component type
    let attendeesArray = [];
    if (isRestaurant) {
      attendeesArray = cd.restaurant_attendees || cd.attendees || cd.guests || [];
    } else if (isMeeting) {
      attendeesArray = cd.meeting_attendees || cd.attendees || [];
    } else if (isChauffeur) {
      attendeesArray = cd.passengers || [];
    }

    attendeesArray.forEach(att => {
      let name = null;
      if (typeof att === 'string') {
        name = formatPassengerName(att);
      } else if (att.name || att.attendee_name || att.guest_name || att.passenger_name) {
        name = formatPassengerName(att.name || att.attendee_name || att.guest_name || att.passenger_name);
      } else if (att.first_name || att.last_name || att.firstName || att.lastName) {
        name = formatPassengerName(`${att.first_name || att.firstName || ''} ${att.last_name || att.lastName || ''}`.trim());
      } else if (att.executive_id && executives) {
        // Look up executive name by ID
        const exec = executives.find(e => e.id === att.executive_id);
        if (exec) {
          name = exec.full_name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim();
        }
      }
      if (name && !allAttendees.includes(name)) {
        allAttendees.push(name);
      }
    });

    // Also check for reservation holder (restaurants) or organizer (meetings)
    if (isRestaurant) {
      const reservationHolder = cd.reservation_holder || cd.reservation_name;
      if (reservationHolder) {
        const name = formatPassengerName(reservationHolder);
        if (name && !allAttendees.includes(name)) {
          allAttendees.unshift(name); // Add to front as likely primary
        }
      }
    }

    // For chauffeur, also check single passenger_name field
    if (isChauffeur && allAttendees.length === 0) {
      const singlePassenger = cd.passenger_name || cd.traveler_name;
      if (singlePassenger) {
        const name = formatPassengerName(singlePassenger);
        if (name) {
          allAttendees.push(name);
        }
      }
    }

    if (allAttendees.length === 0) return { primaryName: null, additionalNames: [] };

    // If a selectedExecutiveId is provided, try to match and prioritize that executive
    let primaryName = allAttendees[0];
    let additionalNames = allAttendees.slice(1);

    if (selectedExecutiveId && executives && executives.length > 0) {
      const selectedExec = executives.find(e => e.id === selectedExecutiveId);
      if (selectedExec) {
        const execName = selectedExec.full_name || `${selectedExec.first_name || ''} ${selectedExec.last_name || ''}`.trim();
        // Find if any attendee name matches the selected executive
        const matchIndex = allAttendees.findIndex(aName => {
          const aNameLower = aName.toLowerCase();
          const execNameLower = execName.toLowerCase();
          const firstNameLower = (selectedExec.first_name || '').toLowerCase();
          const lastNameLower = (selectedExec.last_name || '').toLowerCase();
          return aNameLower === execNameLower ||
                 (firstNameLower && lastNameLower && aNameLower.includes(firstNameLower) && aNameLower.includes(lastNameLower)) ||
                 (aNameLower.split(' ')[0] && execNameLower.includes(aNameLower.split(' ')[0]));
        });
        if (matchIndex >= 0) {
          primaryName = allAttendees[matchIndex];
          additionalNames = allAttendees.filter((_, i) => i !== matchIndex);
        }
      }
    }

    return { primaryName, additionalNames };
  };

  const { primaryName: attendeePrimaryName, additionalNames: attendeeAdditionalNames } = getAttendeesDisplayInfo();
  const startDateTime = getComponentStartDatetime(component);
  // Use timezone-preserving format for rental car events to match the pickup/dropoff location time
  const isRentalEvent = component.isRentalCarEvent || component.component_type === 'rental_car' || component.component_type === 'car_rental';
  const dateDisplay = isRentalEvent ? formatWeekdayDatePreserveTimezone(startDateTime, prefs) : formatDateDisplay(startDateTime, prefs);
  const timeDisplay = formatTimePreserveTimezone(startDateTime, prefs);

  // Matches original ExecDashboard2 ComponentCard
  return (
    <div
      ref={cardRef}
      className={`group relative bg-ui-surface rounded-2xl border border-ui-line p-5 hover:${configModern.border} transition-all duration-300 hover:shadow-2xl min-h-[140px]`}
      data-component-id={component.id}
      data-trip-id={component.tripId}
    >
      {/* Floating sticky notes */}
      {stickyNotes && stickyNotes.length > 0 && stickyNotes.map(note => (
        <div key={note.id} className="absolute z-20 pointer-events-auto" style={{ left: `${note.position?.offset_x || 5}%`, top: `${note.position?.offset_y || 5}%` }}>
          <StickyNote note={note} currentUserId={currentUserId} onToggleExpand={onToggleNoteExpand} onEdit={onEditNote} onDelete={onDeleteNote} isDraggable={true} isFloating={true} containerRef={cardRef} currentComponentId={component.id} onPositionChange={onNotePositionChange} onMoveToComponent={onNoteMoveToComponent} />
        </div>
      ))}

      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${configModern.gradient} shadow-lg`}>
          <Icon className="w-5 h-5 text-ui-on-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${configModern.bgLight} ${configModern.text} capitalize`}>
              {component.component_type?.replace(/_/g, ' ')}
            </span>
            {component.isConfidential ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenConfidentiality) {
                    onOpenConfidentiality({
                      isUnfiled: false,
                      tripId: component.tripId,
                      componentId: component.originalId || component.id,
                      title: getTitle()
                    });
                  }
                }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-ui-amber-bg text-ui-amber-fg border border-ui-amber-line hover:bg-ui-amber-line transition-colors cursor-pointer"
                title="Click to manage confidentiality settings"
              >
                <Lock className="w-3 h-3" />
                Confidential
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenConfidentiality) {
                    onOpenConfidentiality({
                      isUnfiled: false,
                      tripId: component.tripId,
                      componentId: component.originalId || component.id,
                      title: getTitle()
                    });
                  }
                }}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-ui-hover text-ui-ink-3 border border-ui-control hover:bg-ui-line transition-colors cursor-pointer"
                title="Click to manage confidentiality settings"
              >
                <LockOpen className="w-3 h-3" />
              </button>
            )}
          </div>
          <h3 className="text-lg font-semibold text-ui-on-primary mt-2 truncate">{getTitle()}</h3>
          {(getAddress() || getLocation()) && (
            <div className="flex items-center gap-1 text-sm text-ui-ink-3 mt-1">
              <MapPin className="w-3.5 h-3.5" />
              <span className="truncate">{getAddress() || getLocation()}</span>
            </div>
          )}
          {renterName && (
            <div className="flex items-center gap-1 text-sm text-ui-ink-3 mt-1">
              <User className="w-3.5 h-3.5" />
              <span className="truncate">{renterName}</span>
            </div>
          )}
          {attendeePrimaryName && (
            <div className="mt-1">
              <PassengerNameList
                names={[attendeePrimaryName, ...attendeeAdditionalNames]}
                variant="modern"
                size="sm"
                IconComponent={Users}
              />
            </div>
          )}
          <div className="flex items-center gap-1 text-sm text-ui-muted mt-2">
            <Clock className="w-3.5 h-3.5" />
            <span>{dateDisplay}{timeDisplay ? ` at ${timeDisplay}` : ''}</span><SourceDocumentLinks component={component} />
          </div>
          <div className="mt-2">
            {/* Tappable trip row: flex makes the link block-level so `truncate`
                actually clips (inline anchors can't hide overflow, which let
                long program titles escape the card) */}
            {canViewTrip ? (
              <TripLink
                to={`/Trip/${component.tripId}`}
                title={component.tripTitle}
                className="flex items-center gap-1.5 min-w-0 text-xs text-ui-ink-3 hover:text-ui-link transition-colors"
              >
                <Briefcase className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{component.tripTitle || 'Trip'}</span>
                <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
              </TripLink>
            ) : (
              <span className="flex items-center gap-1.5 min-w-0 text-xs text-ui-muted" title={component.tripTitle}>
                <Briefcase className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{component.tripTitle || 'Trip'}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onReportFeedback && (
            <button onClick={() => onReportFeedback(component)} className="p-2 text-ui-amber-fg hover:text-ui-amber-fg hover:bg-ui-hover rounded-lg transition-colors" title="Report parsing issue">
              <AlertTriangle className="w-4 h-4" />
            </button>
          )}
          {canEditTrip && (
            <TripLink to={`/Trip/${component.tripId}?editComponent=${component.originalId || component.id}`} className="p-2 text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover rounded-lg transition-colors" title="Edit">
              <Edit2 className="w-4 h-4" />
            </TripLink>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENT: InboxCard (Theme-Aware) - For EA Inbox items
// ============================================================================

const InboxCard = ({ item, executives, speakers = [], isSpeakerBureau = false, onSetup, trips, selectedExecutiveId, selectedTripId, onExecutiveSelect, onTripSelect, onFile, onDelete, onOpenConfidentiality, isFiling, isDeleting, prefs }) => {
  // In speaker_bureau mode the "assign" list is the new Speaker object.
  const assignList = isSpeakerBureau ? speakers : executives;
  const isSpeakerMeeting = item.component_type === 'speaker_meeting';
  // Component types eligible for the one-click speaker + program quick-add:
  // the meeting itself plus travel components that carry a guest/passenger/driver.
  const QUICK_ADD_TYPES = ['speaker_meeting', 'hotel', 'commercial_flight', 'private_flight', 'flight', 'car_rental', 'rental_car'];
  const canQuickAdd = QUICK_ADD_TYPES.includes(item.component_type);
  const { labels } = useParadigm();
  const cd = item.component_data || {};
  const configModern = componentTypeConfigModern[item.component_type] || componentTypeConfigModern.general;
  const Icon = configModern.icon || Calendar;

  const getTitle = () => {
    if (item.component_type === 'commercial_flight' || item.component_type === 'private_flight' || item.component_type === 'flight') {
      const legs = cd.flight_legs || cd.legs || cd.private_flight_legs || [];
      if (legs.length > 0) {
        const airports = legs.flatMap(leg => [
          leg.departure_airport || leg.departure_airport_code || leg.departure_airport_icao || '',
          leg.arrival_airport || leg.arrival_airport_code || leg.arrival_airport_icao || ''
        ]).filter((v, i, a) => v && (i === 0 || a[i-1] !== v));
        if (airports.length > 0) return airports.join(' - ');
      }
    }
    // The component's own title is what the parser worked out (for an event,
    // the event name). Preferring the component type over it is why a zoo
    // ticket showed up on the dashboard as the single word "event".
    return (
      cd.hotel_name ||
      cd.restaurant_name ||
      cd.rental_company ||
      item.title ||
      cd.event_name ||
      cd.title ||
      item.component_type?.replace(/_/g, ' ') ||
      'Item'
    );
  };

  // Get dates for display
  const getDateDisplay = () => {
    const isHotel = item.component_type === 'hotel';
    const isCarRental = item.component_type === 'car_rental' || item.component_type === 'rental_car';
    const isFlight = item.component_type === 'commercial_flight' || item.component_type === 'private_flight' || item.component_type === 'flight';
    const startDt = item.start_datetime;
    const endDt = item.end_datetime;

    // For flights, show first leg departure to last leg arrival with times
    if (isFlight) {
      const legs = cd.flight_legs || cd.legs || cd.private_flight_legs || [];
      if (legs.length > 0) {
        const firstLeg = legs[0];
        const lastLeg = legs[legs.length - 1];

        // Build datetime from separate date and time fields if needed
        let departureDt = firstLeg.departure_datetime;
        if (!departureDt && firstLeg.departure_date) {
          departureDt = firstLeg.departure_time
            ? `${firstLeg.departure_date}T${firstLeg.departure_time}:00`
            : firstLeg.departure_date;
        }

        let arrivalDt = lastLeg.arrival_datetime;
        if (!arrivalDt && lastLeg.arrival_date) {
          arrivalDt = lastLeg.arrival_time
            ? `${lastLeg.arrival_date}T${lastLeg.arrival_time}:00`
            : lastLeg.arrival_date;
        }

        if (departureDt && arrivalDt) {
          return `${formatDateTime(departureDt, prefs)} - ${formatDateTime(arrivalDt, prefs)}`;
        } else if (departureDt) {
          return formatDateTime(departureDt, prefs);
        }
      }
      // Fall back to start/end datetime if no legs
      if (startDt && endDt) {
        return `${formatDateTime(startDt, prefs)} - ${formatDateTime(endDt, prefs)}`;
      } else if (startDt) {
        return formatDateTime(startDt, prefs);
      }
      return null;
    }

    // Car rentals show date + time for pickup/dropoff (preserve timezone from ISO string)
    if (isCarRental && startDt && endDt) {
      return `${formatDateTimePreserveTimezone(startDt, prefs)} - ${formatDateTimePreserveTimezone(endDt, prefs)}`;
    } else if (isCarRental && startDt) {
      return formatDateTimePreserveTimezone(startDt, prefs);
    }

    // Hotels show date + time for check-in/check-out
    if (isHotel && startDt && endDt) {
      return `${formatDateTime(startDt, prefs)} - ${formatDateTime(endDt, prefs)}`;
    } else if (isHotel && startDt) {
      return formatDateTime(startDt, prefs);
    } else if (startDt) {
      return formatDate(startDt, prefs);
    }
    return null;
  };

  // Get guest/passenger/driver names (normalized from airline format if needed)
  const getNames = () => {
    const isCarRental = item.component_type === 'car_rental' || item.component_type === 'rental_car';

    // For car rentals, check driver_name first
    if (isCarRental) {
      if (cd.driver_name) return formatPassengerName(cd.driver_name);
      if (cd.traveler_name) return formatPassengerName(cd.traveler_name);
    }

    // Try various name fields
    const guests = cd.guests || cd.passengers || [];
    if (guests.length > 0) {
      return guests.map(g => {
        // Handle both string values and objects
        const rawName = typeof g === 'string' ? g : (g.name || g.guest_name || `${g.first_name || ''} ${g.last_name || ''}`.trim());
        return formatPassengerName(rawName);
      }).filter(Boolean).join(', ');
    }
    // Single guest name
    if (cd.guest_name) return formatPassengerName(cd.guest_name);
    if (cd.passenger_name) return formatPassengerName(cd.passenger_name);
    if (cd.traveler_name) return formatPassengerName(cd.traveler_name);
    return null;
  };

  // Get document info for View Original link
  const getDocumentInfo = () => {
    const attachedFiles = cd.attached_files || [];
    if (attachedFiles.length > 0) {
      const file = attachedFiles[0];
      const source = file.source || 'attachment';
      const isEmailBody = source === 'inbound_email_body';

      if (isEmailBody && file.parsingLogId) {
        return { url: `/parsing-logs/${file.parsingLogId}/email`, label: 'View Email' };
      } else {
        const docId = file.documentId || file.id;
        if (docId) {
          return { url: `/files/${docId}/view`, label: 'View Document' };
        }
      }
    }
    return null;
  };

  // Get address without country to save space
  const getAddress = () => {
    let addr = cd.address || cd.full_address;
    if (!addr) return null;

    // Lead with the venue when we know it: the street alone ("800 Cherokee
    // Avenue SE") does not tell you the booking is at Zoo Atlanta.
    const venue = cd.venue_name || cd.event_venue;
    if (venue && !addr.trim().toLowerCase().startsWith(venue.trim().toLowerCase())) {
      addr = `${venue}, ${addr}`;
    }

    // If we know the country, strip it from the end
    const country = cd.country;
    if (country && addr.toLowerCase().includes(country.toLowerCase())) {
      // Remove country and any trailing comma/space
      const countryRegex = new RegExp(',?\\s*' + country.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*$', 'i');
      addr = addr.replace(countryRegex, '').trim();
    }

    // Also try to remove common country patterns at the end
    addr = addr.replace(/,?\s*(United States of America|United States|USA|US|Canada|UK|United Kingdom)\s*$/i, '').trim();

    // Remove trailing comma if any
    addr = addr.replace(/,\s*$/, '');

    return addr || null;
  };

  const canFile = selectedExecutiveId || selectedTripId;
  const dateDisplay = getDateDisplay();
  const names = getNames();
  const docInfo = getDocumentInfo();
  const address = getAddress();

  // min-w-0 lets the card shrink within its grid track (grid items default to
  // min-width:auto, which otherwise forces the card wider than its column when
  // a <select> option is long); overflow-hidden keeps any residual content from
  // spilling over the neighboring card and covering its File button.
  const cardClasses = 'bg-ui-surface backdrop-blur-xl rounded-2xl border border-ui-amber-line p-5 shadow-ui-card min-w-0 overflow-hidden';

  return (
    <div className={cardClasses}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${configModern.gradient}`}>
          <Icon className="w-5 h-5 text-ui-on-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-ui-amber-bg text-ui-amber-fg">
              Needs Assignment
            </span>
            {item.is_confidential_inbox ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenConfidentiality && onOpenConfidentiality({
                    isUnfiled: true,
                    componentId: item.id,
                    componentType: item.component_type,
                    isConfidential: true,
                    accessList: []
                  });
                }}
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full cursor-pointer transition-colors bg-ui-amber-bg text-ui-amber-fg hover:bg-ui-amber-line border border-ui-amber-line"
                title="Click to manage confidentiality settings"
              >
                <Lock className="h-3 w-3" />
                Confidential
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenConfidentiality && onOpenConfidentiality({
                    isUnfiled: true,
                    componentId: item.id,
                    componentType: item.component_type,
                    isConfidential: false,
                    accessList: []
                  });
                }}
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full cursor-pointer transition-colors bg-ui-hover text-ui-ink-3 hover:bg-ui-raised border border-ui-control"
                title="Click to manage confidentiality settings"
              >
                <LockOpen className="h-3 w-3" />
              </button>
            )}
          </div>
          <h3 className="text-lg font-semibold mt-2 truncate text-ui-ink">{getTitle()}</h3>
        </div>
        <button
          onClick={() => onDelete(item.id)}
          disabled={isDeleting}
          className="p-2 rounded-lg transition-colors text-ui-muted hover:text-ui-red-fg hover:bg-ui-red-bg"
        >
          {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Details - Each item on separate line */}
      <div className="flex flex-col gap-1 text-xs mb-3 text-ui-ink-3">
        {dateDisplay && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3 flex-shrink-0" />
            {dateDisplay}
          </span>
        )}
        {address && (
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            {address}
          </span>
        )}
        {names && (
          <span className="flex items-center gap-1">
            <User className="h-3 w-3 flex-shrink-0" />
            {names}
          </span>
        )}
        {docInfo && (
          <a
            href={`${getApiBase()}${docInfo.url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-ui-link hover:text-ui-blue-fg"
            onClick={(e) => e.stopPropagation()}
          >
            <FileText className="h-3 w-3 flex-shrink-0" />
            <span>{docInfo.label}</span>
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>

      <div className="space-y-3 pt-4 border-t border-ui-divider">
        {/* One-click: create/match the speaker + program and file this item into it */}
        {isSpeakerBureau && canQuickAdd && (
          <>
            <button
              onClick={() => onSetup && onSetup()}
              disabled={isFiling}
              className="w-full h-10 px-3 text-sm font-semibold rounded-xl flex items-center justify-center gap-2 transition-all bg-gradient-to-r from-ui-red-strong to-ui-pink-strong hover:opacity-90 text-ui-on-primary disabled:opacity-50"
            >
              {isFiling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Set up speaker &amp; program
            </button>
            <div className="flex items-center gap-2 text-xs text-ui-muted">
              <span className="flex-1 h-px bg-current opacity-20" />
              or assign to existing
              <span className="flex-1 h-px bg-current opacity-20" />
            </div>
          </>
        )}
        <select
          value={selectedExecutiveId || ''}
          onChange={(e) => onExecutiveSelect(item.id, e.target.value)}
          className="w-full min-w-0 h-10 px-3 text-sm rounded-xl focus:outline-none focus:ring-2 bg-ui-sunk border border-ui-control text-ui-ink focus:ring-ui-amber-solid/50 focus:border-ui-amber-solid/50"
        >
          <option value="">{'Assign to ' + labels.executive.toLowerCase() + '...'}</option>
          {assignList.map((exec) => {
            const name = exec.full_name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim();
            const suffix = exec.company || exec.affiliation;
            return (
              <option key={exec.id} value={exec.id}>
                {suffix ? `${name} (${suffix})` : name}
              </option>
            );
          })}
        </select>

        <div className="space-y-2 min-w-0">
          <select
            value={selectedTripId || ''}
            onChange={(e) => onTripSelect(item.id, e.target.value)}
            className="flex-1 min-w-0 h-10 px-3 text-sm rounded-xl focus:outline-none focus:ring-2 bg-ui-sunk border border-ui-control text-ui-ink focus:ring-ui-amber-solid/50 focus:border-ui-amber-solid/50"
          >
            <option value="">Assign to trip...</option>
            {trips.map((trip) => (
              <option key={trip.id} value={trip.id}>
                {trip.title || 'Untitled Trip'}
              </option>
            ))}
          </select>
          {!isSpeakerBureau ? <InlineAssignment componentId={item.id} tripId={selectedTripId}
            executiveId={selectedExecutiveId} busy={isFiling} onApply={preview => onFile(item.id, preview)} /> : (
          <button
            onClick={() => onFile(item.id)}
            disabled={!canFile || isFiling}
            className="h-10 px-4 font-medium rounded-xl transition-all duration-200 flex items-center gap-2 flex-shrink-0 bg-gradient-to-r from-ui-amber-strong to-ui-orange-strong hover:opacity-90 disabled:from-ui-faint disabled:to-ui-dash text-ui-on-primary disabled:cursor-not-allowed"
          >
            {isFiling ? <Loader2 className="w-4 h-4 animate-spin" /> : <>File <ArrowRight className="w-4 h-4" /></>}
          </button>)}

        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT: ExecDashboardV2
// ============================================================================

const ExecDashboardV2 = () => {
  const { user, setUser, actingOrganization } = useAuth();
  const { labels, isSpeakerBureau } = useParadigm();
  const navigate = useNavigate();
  const location = useLocation();
  const isExecutiveUser = user?.role === 'executive';
  const linkedExecId = user?.linked_executive_id || null;
  // Speaker portal accounts: external speakers with a strictly view-only dashboard
  const isSpeakerUser = user?.role === 'speaker';
  const linkedSpeakerId = user?.linked_speaker_id || null;
  // Users whose dashboard is scoped to themselves (no inbox, no create/edit)
  const isRestrictedUser = isExecutiveUser || isSpeakerUser;

  // State
  const [executives, setExecutives] = useState([]);
  const [speakers, setSpeakers] = useState([]); // speaker_bureau: assign-to-speaker source
  const [setupItem, setSetupItem] = useState(null); // inbox item being set up via quick-program wizard
  // speaker_bureau dashboard scoping: search-selected speaker, or "show all programs"
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [showNewProgram, setShowNewProgram] = useState(false); // header "New" program wizard
  const [selectedExecutiveId, setSelectedExecutiveId] = useState(() => {
    return user?.ui_preferences?.exec_dashboard_selected_executive || null;
  });
  const [trips, setTrips] = useState([]);
  const [allTrips, setAllTrips] = useState([]);
  const [unfiledItems, setUnfiledItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [unfiledLoading, setUnfiledLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignmentNotice, setAssignmentNotice] = useState('');

  // EA Inbox state
  const { items: eaInboxItems, setItems: setEaInboxItems, loading: eaInboxLoading,
    refresh: fetchEAInboxItems } = useDashboardInbox(user?.id,
      actingOrganization?.id || user?.organization_id,
      isSpeakerBureau ? null : selectedExecutiveId, !isRestrictedUser);
  const [selectedExecutives, setSelectedExecutives] = useState({});
  const [selectedTrips, setSelectedTrips] = useState({});
  const [assigningId, setAssigningId] = useState(null);
  const [filingId, setFilingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Layout mode: 'grid' or 'list' - persisted in user preferences
  const [layoutMode, setLayoutMode] = useState(() => {
    return user?.ui_preferences?.exec_dashboard_v2_layout || 'list';
  });

  // Timeline filter - persisted in localStorage
  const [timelineFilter, setTimelineFilter] = useState(() => {
    try {
      const saved = localStorage.getItem('execDashboard_timelineFilter');
      if (saved && ['today', 'tomorrow', 'upcoming', 'all', 'range'].includes(saved)) {
        return saved;
      }
    } catch (e) {
      // localStorage not available
    }
    return 'upcoming';
  });
  const [timelineVisibleCount, setTimelineVisibleCount] = useState(15);

  // Journey layout: one card for travellers on the same series of flights
  const [consolidateFlights, setConsolidateFlights] = useState(() => {
    try { return localStorage.getItem('execDashboard_consolidateFlights') === 'true'; } catch (e) { return false; }
  });
  const toggleConsolidateFlights = () => setConsolidateFlights((on) => {
    try { localStorage.setItem('execDashboard_consolidateFlights', String(!on)); } catch (e) { /* not available */ }
    return !on;
  });

  // Custom date-range filter - persisted in localStorage
  const [timelineDateRange, setTimelineDateRange] = useState(() => {
    try {
      const saved = localStorage.getItem('execDashboard_timelineDateRange');
      if (saved) {
        const parsed = JSON.parse(saved);
        const from = parsed.from ? new Date(parsed.from) : undefined;
        const to = parsed.to ? new Date(parsed.to) : undefined;
        if ((from && !Number.isNaN(from.getTime())) || (to && !Number.isNaN(to.getTime()))) {
          return { from, to };
        }
      }
    } catch (e) {
      // ignore
    }
    return { from: undefined, to: undefined };
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Persist timeline filter to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('execDashboard_timelineFilter', timelineFilter);
    } catch (e) {
      // localStorage not available
    }
  }, [timelineFilter]);

  // Persist timeline date range to localStorage when it changes
  useEffect(() => {
    try {
      const payload = {
        from: timelineDateRange?.from ? timelineDateRange.from.toISOString() : null,
        to: timelineDateRange?.to ? timelineDateRange.to.toISOString() : null,
      };
      localStorage.setItem('execDashboard_timelineDateRange', JSON.stringify(payload));
    } catch (e) {
      // localStorage not available
    }
  }, [timelineDateRange]);

  // Filter states
  const [selectedTripIds, setSelectedTripIds] = useState([]);
  const [selectedComponentTypes, setSelectedComponentTypes] = useState(
    COMPONENT_TYPE_OPTIONS.map(opt => opt.value)
  );

  // Trip carousel state
  const [tripCarouselIndex, setTripCarouselIndex] = useState(0);

  // Sticky notes state
  const [stickyNotes, setStickyNotes] = useState({});
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteTargetTripId, setNoteTargetTripId] = useState(null);
  const [noteTargetComponentId, setNoteTargetComponentId] = useState(null);
  const [isPlacingNote, setIsPlacingNote] = useState(false);
  const [pendingNotePosition, setPendingNotePosition] = useState(null);

  // Parsing feedback modal state
  const [feedbackComponent, setFeedbackComponent] = useState(null);

  // Trip modal states
  const [showCreateTripModal, setShowCreateTripModal] = useState(false);
  const [showEditTripModal, setShowEditTripModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);

  // Confidentiality settings modal state
  const [showConfidentialityModal, setShowConfidentialityModal] = useState(false);
  const [confidentialityTarget, setConfidentialityTarget] = useState(null);

  // Get theme-specific classes
  const containerClasses = getContainerClasses();
  const textClasses = getTextClasses();
  const buttonClasses = getButtonClasses();
  const inputClasses = getInputClasses();

  // Save layout preference to database
  const handleLayoutChange = async (mode) => {
    setLayoutMode(mode);
    try {
      const res = await axios.put('/users/me/preferences', { exec_dashboard_v2_layout: mode });
      if (user && setUser) {
        setUser({ ...user, ui_preferences: res.data });
      }
    } catch (err) {
      console.error('Failed to save layout preference:', err);
    }
  };

  // Sync layout preference when user data loads
  useEffect(() => {
    const savedPref = user?.ui_preferences?.exec_dashboard_v2_layout;
    if (savedPref) {
      setLayoutMode(savedPref);
    }
  }, [user?.ui_preferences?.exec_dashboard_v2_layout]);

  // Sync selected executive when user preferences load
  useEffect(() => {
    const savedExecId = user?.ui_preferences?.exec_dashboard_selected_executive;
    if (executives.length > 0) {
      const currentExecValid = selectedExecutiveId && executives.some(e => e.id === selectedExecutiveId);
      if (currentExecValid) return;
      if (savedExecId) {
        const execExists = executives.some(e => e.id === savedExecId);
        if (execExists) {
          setSelectedExecutiveId(savedExecId);
          return;
        }
      }
      setSelectedExecutiveId(executives[0].id);
    }
  }, [user?.ui_preferences?.exec_dashboard_selected_executive, executives, selectedExecutiveId]);

  // Fetch executives
  const fetchExecutives = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/executives`, { headers });
      const execs = Array.isArray(response.data) ? response.data : [];
      setExecutives(execs);
    } catch (err) {
      console.error('Error fetching executives:', err);
      setError('Failed to load executives');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch speakers for the speaker_bureau "Assign to speaker" dropdown
  const fetchSpeakers = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/speakers`, { headers, params: { limit: 500 } });
      setSpeakers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching speakers:', err);
    }
  }, []);

  // Fetch trips for selected executive
  const fetchTrips = useCallback(async (executiveId) => {
    // speaker_bureau shows org-shared programs and does NOT depend on a selected
    // executive; for ea_travel an executive is still required.
    if (!executiveId && !isSpeakerBureau) return;
    try {
      setTripsLoading(true);
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      // Archived trips are never rendered here, so do not fetch them.
      const response = await axios.get(`${apiBase}/trips?limit=100`, { headers });
      const allTripsData = Array.isArray(response.data) ? response.data : [];
      setAllTrips(allTripsData.filter(trip => !trip.archived));
      const visibleTrips = allTripsData.filter(trip => {
        if (trip.archived) return false;
        if (isSpeakerBureau) {
          // Scope to the searched speaker; "Show all" reveals the whole pipeline.
          const sids = trip.speaker_ids || [];
          if (showAllPrograms) return sids.length > 0;
          if (selectedSpeaker?.id) return sids.includes(selectedSpeaker.id);
          return false; // nothing until a speaker is picked or "Show all"
        }
        const execIds = trip.executive_ids || [];
        return Boolean(executiveId && execIds.includes(executiveId));
      });
      setTrips(visibleTrips);
    } catch (err) {
      console.error('Error fetching trips:', err);
    } finally {
      setTripsLoading(false);
    }
  }, [isSpeakerBureau, showAllPrograms, selectedSpeaker]);

  // Fetch unfiled items for selected executive
  const fetchUnfiledItems = useCallback(async (executiveId) => {
    if (!executiveId) return;
    try {
      setUnfiledLoading(true);
      const result = await listUnfilledComponents(executiveId);
      setUnfiledItems(result.unfilled_components || []);
    } catch (err) {
      console.error('Error fetching unfiled items:', err);
    } finally {
      setUnfiledLoading(false);
    }
  }, []);

  // Fetch sticky notes for all executive trips in a single request
  const fetchStickyNotes = useCallback(async (tripsList) => {
    if (!tripsList || tripsList.length === 0) return;
    const notesMap = {};
    try {
      const byTrip = await listStickyNotesForTrips(tripsList.map(trip => trip.id));
      for (const trip of tripsList) {
        notesMap[trip.id] = (byTrip[trip.id] || []).filter(note => {
          if (note.is_confidential && note.created_by_id !== user?.id) return false;
          const posType = note.position?.position_type;
          return (posType === 'inside' || posType === 'attached') && note.position?.attached_to_component_id;
        });
      }
    } catch (err) {
      console.error('Error fetching sticky notes:', err);
      for (const trip of tripsList) notesMap[trip.id] = [];
    }
    setStickyNotes(notesMap);
  }, [user?.id]);

  // Coalesce bursts of refreshes (an assignment emits several SSE events and the
  // filing handler refreshes too) into one request per source.
  const refreshTimers = useRef({});
  const latestScope = useRef({});
  latestScope.current = { selectedExecutiveId, isSpeakerBureau };
  const scheduleRefresh = useCallback((key, run, delay = 400) => {
    clearTimeout(refreshTimers.current[key]);
    refreshTimers.current[key] = setTimeout(run, delay);
  }, []);
  useEffect(() => {
    const timers = refreshTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);
  const scheduleTripsRefresh = useCallback(() => scheduleRefresh('trips', () => {
    const { selectedExecutiveId: execId, isSpeakerBureau: bureau } = latestScope.current;
    fetchTrips(bureau ? null : execId);
  }), [scheduleRefresh, fetchTrips]);
  const scheduleInboxRefresh = useCallback(() => scheduleRefresh('inbox', () => {
    fetchEAInboxItems();
    const { selectedExecutiveId: execId } = latestScope.current;
    if (execId) fetchUnfiledItems(execId);
  }), [scheduleRefresh, fetchEAInboxItems, fetchUnfiledItems]);

  // Get notes for a specific component
  const getNotesForComponent = (tripId, componentId) => {
    const tripNotes = stickyNotes[tripId] || [];
    return tripNotes.filter(note => note.position?.attached_to_component_id === componentId);
  };

  // Initial data fetch
  useEffect(() => {
    fetchExecutives();
    if (isSpeakerBureau) {
      fetchSpeakers();
    }
  }, [fetchExecutives, fetchSpeakers, isSpeakerBureau]);

  // Speaker portal accounts: auto-select their own profile (the backend only
  // returns their linked profile from /speakers, so there is nothing to search)
  useEffect(() => {
    if (isSpeakerUser && !selectedSpeaker && speakers.length > 0) {
      const own = speakers.find(s => s.id === linkedSpeakerId) || speakers[0];
      setSelectedSpeaker(own);
    }
  }, [isSpeakerUser, speakers, selectedSpeaker, linkedSpeakerId]);

  // Fetch trips: speaker_bureau scopes by the searched speaker (or "show all");
  // ea_travel scopes by the selected executive.
  useEffect(() => {
    if (isSpeakerBureau) {
      fetchTrips(null);
    } else if (selectedExecutiveId) {
      fetchTrips(selectedExecutiveId);
      fetchUnfiledItems(selectedExecutiveId);
    }
  }, [selectedExecutiveId, isSpeakerBureau, fetchTrips, fetchUnfiledItems]);

  // Fetch sticky notes when trips change
  useEffect(() => {
    if (trips.length > 0) {
      fetchStickyNotes(trips);
    }
  }, [trips, fetchStickyNotes]);

  // Reset carousel when executive changes
  useEffect(() => {
    setTripCarouselIndex(0);
  }, [selectedExecutiveId]);

  // Handle SSE events for unfiled components (real-time updates)
  const handleUnfiledComponentEvent = useCallback((event) => {
    scheduleInboxRefresh();
    if (selectedExecutiveId && event.type === SSEEventType.UNFILED_COMPONENT_ASSIGNED) scheduleTripsRefresh();
  }, [selectedExecutiveId, scheduleInboxRefresh, scheduleTripsRefresh]);

  useUnfiledComponentEvents(handleUnfiledComponentEvent, [selectedExecutiveId]);

  // Gate, terminal, aircraft, status, and timing details can arrive shortly
  // before departure. Refresh the executive timeline when FlightAware pushes
  // an update so the card reflects those assignments without a page reload.
  const handleFlightUpdateEvent = useCallback(() => {
    scheduleTripsRefresh();
  }, [scheduleTripsRefresh]);

  useFlightUpdateEvents(handleFlightUpdateEvent, [selectedExecutiveId, isSpeakerBureau]);

  // Handle opening confidentiality settings modal
  const handleOpenConfidentialityModal = useCallback((target) => {
    setConfidentialityTarget(target);
    setShowConfidentialityModal(true);
  }, []);

  // Handle confidentiality settings update
  const handleConfidentialityUpdate = useCallback(({ is_confidential, access_list }) => {
    if (!confidentialityTarget) return;

    if (confidentialityTarget.isUnfiled) {
      // Update unfiled component in state
      setEaInboxItems(prev => prev.map(item =>
        item.id === confidentialityTarget.componentId
          ? { ...item, is_confidential_inbox: is_confidential }
          : item
      ));
      setUnfiledItems(prev => prev.map(item =>
        item.id === confidentialityTarget.componentId
          ? { ...item, is_confidential_inbox: is_confidential }
          : item
      ));
    } else {
      // For trip components, refresh trips to get updated data
      if (selectedExecutiveId) {
        fetchTrips(selectedExecutiveId);
      }
    }

    setShowConfidentialityModal(false);
    setConfidentialityTarget(null);
  }, [confidentialityTarget, selectedExecutiveId, fetchTrips]);

  // Handle executive change
  const handleExecutiveChange = async (e) => {
    const newExecId = e.target.value;
    setSelectedExecutiveId(newExecId);
    try {
      const res = await axios.put('/users/me/preferences', { exec_dashboard_selected_executive: newExecId });
      if (user && setUser) {
        setUser({ ...user, ui_preferences: res.data });
      }
    } catch (err) {
      console.error('Failed to save executive preference:', err);
    }
  };

  // Handle filing EA inbox items
  const handleFile = async (itemId, acceptedPreview) => {
    // In speaker_bureau mode the first dropdown holds a SPEAKER id, not an executive.
    const pickId = selectedExecutives[itemId];
    const tripId = selectedTrips[itemId];
    const execId = isSpeakerBureau ? null : pickId;
    const speakerId = isSpeakerBureau ? pickId : null;
    if (!execId && !tripId && !speakerId) return;

    try {
      setFilingId(itemId);
      const result = await fileUnfilledComponent(itemId, { executiveId: execId, tripId, acceptedPreview });
      setAssignmentNotice(result.warnings?.join(' ') || (result.updates?.some(r => r.action === 'update') ? 'Booking updated. Previous itineraries and source documents remain available in the booking.' : 'Item assigned.'));
      // Link the chosen speaker to the target trip (engagement association)
      if (speakerId && tripId) {
        try {
          const apiBase = getApiBase();
          const headers = getAuthHeaders();
          const speaker = speakers.find((s) => s.id === speakerId);
          const trip = allTrips.find((t) => t.id === tripId) || {};
          const existingIds = trip.speaker_ids || [];
          if (!existingIds.includes(speakerId)) {
            const name = speaker
              ? (speaker.full_name || `${speaker.first_name || ''} ${speaker.last_name || ''}`.trim())
              : '';
            await axios.put(`${apiBase}/trips/${tripId}`, {
              speaker_ids: [...existingIds, speakerId],
              speaker_names: [...(trip.speaker_names || []), name].filter(Boolean),
            }, { headers });
          }
        } catch (linkErr) {
          console.error('Failed to link speaker to trip:', linkErr);
        }
      }
      setEaInboxItems(prev => prev.filter(item => item.id !== itemId));
      setSelectedExecutives(prev => { const n = {...prev}; delete n[itemId]; return n; });
      setSelectedTrips(prev => { const n = {...prev}; delete n[itemId]; return n; });
      if (execId && execId === selectedExecutiveId) {
        scheduleTripsRefresh();
        scheduleInboxRefresh();
      }
    } catch (err) {
      console.error('Error filing item:', err);
      if (err.message !== 'Assignment cancelled') alert(err.response?.data?.detail || err.message || 'Failed to file item');
    } finally {
      setFilingId(null);
    }
  };

  // Build wizard prefill from a parsed speaker_meeting inbox item
  const buildSpeakerProgramPrefill = (item) => {
    const cd = item.component_data || {};
    const type = item.component_type;
    const isHotel = type === 'hotel';

    // Pull the primary person from whichever field the component carries:
    // meeting speaker / hotel guest / airline passenger / rental driver.
    const firstFromList = (arr) => {
      for (const g of arr || []) {
        if (typeof g === 'string' && g.trim()) return g.trim();
        if (g && typeof g === 'object') {
          const nm = g.name || g.guest_name || `${g.first_name || ''} ${g.last_name || ''}`.trim();
          if (nm && nm.trim()) return nm.trim();
        }
      }
      return '';
    };
    const rawName = cd.speaker_name || cd.driver_name || cd.guest_name
      || cd.passenger_name || cd.traveler_name
      || firstFromList(cd.guests) || firstFromList(cd.passengers) || '';
    const speakerName = rawName ? formatPassengerName(rawName) : '';

    // Sensible program defaults per component type.
    let title = cd.program_topic || '';
    if (!title && isHotel && cd.hotel_name) title = `Program — ${cd.hotel_name}`;
    if (!title) title = item.title || '';

    return {
      speakerName,
      speakerEmail: cd.speaker_email || cd.guest_email || cd.email || '',
      // Hints from parse-time matching so the modal can confirm an existing speaker.
      matchedSpeakerId: cd.speaker_id || null,
      matchSuggestions: cd.speaker_match_suggestions || [],
      program: {
        title,
        start_date: cd.program_date
          || (item.start_datetime ? String(item.start_datetime).slice(0, 10) : '')
          || (cd.start_datetime ? String(cd.start_datetime).slice(0, 10) : '')
          || (cd.check_in_date ? String(cd.check_in_date).slice(0, 10) : ''),
        destination: cd.venue_name || cd.venue_address || cd.location || cd.hotel_city || cd.city || '',
        sponsor: cd.sponsor || '',
        product: cd.product_name || '',
      },
    };
  };

  // After the quick-program wizard creates the speaker + program, file the inbox
  // item into the newly created program so it lands on that trip's timeline.
  const handleProgramCreated = async (data) => {
    const item = setupItem;
    setSetupItem(null);
    fetchSpeakers();
    const newTripId = data?.trip?.id;
    if (item && newTripId) {
      try {
        setFilingId(item.id);
        await fileUnfilledComponent(item.id, { tripId: newTripId });
        setEaInboxItems(prev => prev.filter(i => i.id !== item.id));
      } catch (err) {
        console.error('Error filing item into new program:', err);
        alert('Program created, but filing the item failed. You can assign it from the inbox.');
      } finally {
        setFilingId(null);
      }
    }
    // Scope the dashboard to this speaker so the program shows on return
    if (data?.speaker) setSelectedSpeaker(data.speaker);
    // Navigate to the new program so the creator sees it immediately
    if (newTripId) {
      navigate(`/Trip/${newTripId}`, { state: tripOriginState(location) });
    }
  };

  // Handle deleting EA inbox items
  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      setDeletingId(itemId);
      await deleteUnfilledComponent(itemId);
      setEaInboxItems(prev => prev.filter(item => item.id !== itemId));
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Failed to delete item');
    } finally {
      setDeletingId(null);
    }
  };

  // Handle assigning unfiled items
  const handleAssign = async (itemId) => {
    const tripId = selectedTrips[itemId];
    if (!tripId) return;
    try {
      setAssigningId(itemId);
      await assignUnfilledComponent(itemId, tripId);
      setUnfiledItems(prev => prev.filter(item => item.id !== itemId));
      scheduleTripsRefresh();
    } catch (err) {
      console.error('Error assigning item:', err);
      alert('Failed to assign item');
    } finally {
      setAssigningId(null);
    }
  };

  // Handle reporting parsing feedback
  const handleReportFeedback = (component) => {
    setFeedbackComponent(component);
  };

  // Handle creating notes
  const handleCreateNote = () => {
    setIsPlacingNote(true);
  };

  // Handle clicking on a card to place a note
  const handlePlaceNoteOnCard = (tripId, componentId) => {
    if (!isPlacingNote) return;
    setNoteTargetTripId(tripId);
    setNoteTargetComponentId(componentId);
    setIsPlacingNote(false);
    setNoteModalOpen(true);
  };

  // Handle note modal save
  const handleSaveNote = async (noteData, noteId) => {
    try {
      if (noteId) {
        const updated = await updateStickyNote(noteId, noteData);
        setStickyNotes(prev => {
          const newMap = { ...prev };
          for (const tripId of Object.keys(newMap)) {
            newMap[tripId] = newMap[tripId].map(n => n.id === noteId ? updated : n);
          }
          return newMap;
        });
      } else {
        const position = pendingNotePosition || {
          position_type: 'inside',
          attached_to_component_id: noteTargetComponentId,
          offset_x: 5,
          offset_y: 5,
          rank: 0
        };
        // Executives without read/write on the trip use the exec-note endpoint
        // which is allowed for any trip listing them in executive_ids and
        // triggers an email + in-app notification to their EAs.
        const tripAccessLevel = tripAccessByTripId[noteTargetTripId];
        const useExecPath = isExecutiveUser && tripAccessLevel !== 'read' && tripAccessLevel !== 'write';
        const created = useExecPath
          ? await createExecStickyNote(noteTargetTripId, { ...noteData, position })
          : await createStickyNote(noteTargetTripId, { ...noteData, position });
        setStickyNotes(prev => ({
          ...prev,
          [noteTargetTripId]: [...(prev[noteTargetTripId] || []), created]
        }));
      }
      setNoteModalOpen(false);
      setEditingNote(null);
      setNoteTargetTripId(null);
      setNoteTargetComponentId(null);
      setPendingNotePosition(null);
    } catch (err) {
      console.error('Error saving note:', err);
      throw err;
    }
  };

  // Handle toggle note expansion
  const handleToggleNoteExpand = async (noteId, isExpanded) => {
    try {
      const updated = await updateStickyNote(noteId, { is_expanded: isExpanded });
      setStickyNotes(prev => {
        const newMap = { ...prev };
        for (const tripId of Object.keys(newMap)) {
          newMap[tripId] = newMap[tripId].map(n => n.id === noteId ? updated : n);
        }
        return newMap;
      });
    } catch (err) {
      console.error('Error toggling note expansion:', err);
    }
  };

  // Handle edit note
  const handleEditNote = (note) => {
    for (const [tripId, notes] of Object.entries(stickyNotes)) {
      if (notes.some(n => n.id === note.id)) {
        setNoteTargetTripId(tripId);
        break;
      }
    }
    setEditingNote(note);
    setNoteModalOpen(true);
  };

  // Handle delete note
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    try {
      await deleteStickyNote(noteId);
      setStickyNotes(prev => {
        const newMap = { ...prev };
        for (const tripId of Object.keys(newMap)) {
          newMap[tripId] = newMap[tripId].filter(n => n.id !== noteId);
        }
        return newMap;
      });
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Failed to delete note');
    }
  };

  // Handle note position change
  const handleNotePositionChange = async (noteId, offsetX, offsetY) => {
    try {
      let currentNote = null;
      for (const notes of Object.values(stickyNotes)) {
        currentNote = notes.find(n => n.id === noteId);
        if (currentNote) break;
      }
      if (!currentNote) return;

      const newPosition = { ...currentNote.position, offset_x: offsetX, offset_y: offsetY };
      const updated = await updateStickyNote(noteId, { position: newPosition });
      setStickyNotes(prev => {
        const newMap = { ...prev };
        for (const tripId of Object.keys(newMap)) {
          newMap[tripId] = newMap[tripId].map(n => n.id === noteId ? updated : n);
        }
        return newMap;
      });
    } catch (err) {
      console.error('Error updating note position:', err);
    }
  };

  // Handle note move to component
  const handleNoteMoveToComponent = async (noteId, targetComponentId, offsetX, offsetY) => {
    try {
      let currentNote = null;
      let sourceTripId = null;
      for (const [tripId, notes] of Object.entries(stickyNotes)) {
        currentNote = notes.find(n => n.id === noteId);
        if (currentNote) {
          sourceTripId = tripId;
          break;
        }
      }
      if (!currentNote || !sourceTripId) return;

      let targetTripId = null;
      for (const trip of trips) {
        const hasComponent = trip.components?.some(c => c.id === targetComponentId);
        if (hasComponent) {
          targetTripId = trip.id;
          break;
        }
      }

      if (!targetTripId) {
        const timelineComponents = buildTimeline();
        const targetComp = timelineComponents.find(c => c.id === targetComponentId);
        if (targetComp) targetTripId = targetComp.tripId;
      }

      if (!targetTripId) {
        console.error('Could not find trip for target component:', targetComponentId);
        return;
      }

      const newPosition = {
        position_type: 'inside',
        attached_to_component_id: targetComponentId,
        offset_x: offsetX,
        offset_y: offsetY,
        rank: 0
      };

      const updated = await updateStickyNote(noteId, { position: newPosition });

      if (sourceTripId !== targetTripId) {
        setStickyNotes(prev => {
          const newMap = { ...prev };
          newMap[sourceTripId] = (newMap[sourceTripId] || []).filter(n => n.id !== noteId);
          newMap[targetTripId] = [...(newMap[targetTripId] || []), updated];
          return newMap;
        });
      } else {
        setStickyNotes(prev => {
          const newMap = { ...prev };
          newMap[sourceTripId] = newMap[sourceTripId].map(n => n.id === noteId ? updated : n);
          return newMap;
        });
      }
    } catch (err) {
      console.error('Error moving note to component:', err);
    }
  };

  // Build timeline from trips
  // scopeOverride: build for this executive id, or null for everyone on the trips
  const buildTimeline = useCallback((scopeOverride) => {
    const processed = [];

    // Helper to check if a component is confidential
    const isComponentConfidential = (component, trip) => {
      // Check component's own is_confidential field (new approach)
      if (component.is_confidential) return true;
      // Check trip's component_permission_overrides (legacy approach)
      const overrides = trip?.component_permission_overrides || [];
      const override = overrides.find(o => o.component_id === component.id);
      return override?.is_confidential || false;
    };

    trips.forEach(trip => {
      const tripId = trip.id;
      const tripTitle = trip.title || 'Untitled Trip';
      // The dashboard shows the selected executive's components, not every
      // component of every trip they are on. Trips can be shared, so each
      // component is scoped by its own executive assignment.
      const scopeExecutiveId = scopeOverride !== undefined ? scopeOverride : (isSpeakerBureau ? null : selectedExecutiveId);
      const components = (trip.components || []).filter(
        component => componentAssignedToExecutive(component, scopeExecutiveId)
      );

      components.forEach(component => {
        // Compute confidentiality once per component
        const isConfidential = isComponentConfidential(component, trip);

        // Handle commercial flights
        if (component.component_type === 'commercial_flight' &&
            component.component_data?.flight_legs &&
            component.component_data.flight_legs.length > 0) {

          component.component_data.flight_legs.forEach((leg, legIndex) => {
            const depCode = leg.departure_airport || leg.departure_airport_code || '';
            const arrCode = leg.arrival_airport || leg.arrival_airport_code || '';
            const legTitle = `${depCode || 'TBD'} → ${arrCode || 'TBD'}`;

            processed.push({
              ...component,
              id: `${component.id}-leg-${legIndex}`,
              originalId: component.id,
              title: legTitle,
              start_datetime: leg.departure_datetime || leg.departure_time || component.start_datetime,
              end_datetime: leg.arrival_datetime || leg.arrival_time || component.end_datetime,
              isFlightLeg: true,
              legIndex: legIndex,
              totalLegs: component.component_data.flight_legs.length,
              flightLeg: leg,
              originalComponent: component,
              tripId,
              tripTitle,
              isConfidential
            });
          });
        }
        // Handle private flights
        else if (component.component_type === 'private_flight' &&
            component.component_data?.private_flight_legs &&
            component.component_data.private_flight_legs.length > 0) {

          component.component_data.private_flight_legs.forEach((leg, legIndex) => {
            const depIcao = leg.departure_airport_icao || '';
            const arrIcao = leg.arrival_airport_icao || '';
            const depIata = leg.departure_airport_iata || '';
            const arrIata = leg.arrival_airport_iata || '';
            const depCode = depIcao || depIata;
            const arrCode = arrIcao || arrIata;
            const legTitle = `${depCode || 'TBD'} → ${arrCode || 'TBD'}`;

            // Build departure datetime - try multiple sources
            let depDateTime = null;
            if (leg.departure_datetime) {
              // Already have full datetime
              depDateTime = leg.departure_datetime;
            } else if (leg.departure_date) {
              // Combine date and time
              depDateTime = leg.departure_time
                ? `${leg.departure_date}T${leg.departure_time}${leg.departure_time.length === 5 ? ':00' : ''}`
                : `${leg.departure_date}T00:00:00`;
            } else if (leg.departure_time && component.start_datetime) {
              // Use component start_datetime's date with leg's time
              const baseDate = component.start_datetime.split('T')[0];
              if (baseDate) {
                depDateTime = `${baseDate}T${leg.departure_time}${leg.departure_time.length === 5 ? ':00' : ''}`;
              }
            }

            // Build arrival datetime - try multiple sources
            let arrDateTime = null;
            if (leg.arrival_datetime) {
              arrDateTime = leg.arrival_datetime;
            } else if (leg.arrival_date) {
              arrDateTime = leg.arrival_time
                ? `${leg.arrival_date}T${leg.arrival_time}${leg.arrival_time.length === 5 ? ':00' : ''}`
                : `${leg.arrival_date}T00:00:00`;
            } else if (leg.arrival_time && (depDateTime || component.end_datetime)) {
              // Use departure date or component end_datetime's date with leg's arrival time
              const baseDate = (depDateTime || component.end_datetime || '').split('T')[0];
              if (baseDate) {
                arrDateTime = `${baseDate}T${leg.arrival_time}${leg.arrival_time.length === 5 ? ':00' : ''}`;
              }
            }

            processed.push({
              ...component,
              id: `${component.id}-private-leg-${legIndex}`,
              originalId: component.id,
              title: legTitle,
              start_datetime: depDateTime,
              end_datetime: arrDateTime,
              isFlightLeg: true,
              isPrivateFlightLeg: true,
              isPrivateFlight: true,
              legIndex: legIndex,
              totalLegs: component.component_data.private_flight_legs.length,
              flightLeg: {
                ...leg,
                departure_datetime: depDateTime,
                arrival_datetime: arrDateTime,
              },
              originalComponent: component,
              tripId,
              tripTitle,
              isConfidential
            });
          });
        }
        // Handle hotel check-in/check-out
        else if (component.component_type === 'hotel' &&
            component.component_data?.check_in &&
            component.component_data?.check_out) {

          const hotelName = component.component_data.hotel_name || component.title || 'Hotel';
          const checkInDate = component.component_data.check_in;
          const checkOutDate = component.component_data.check_out;
          const hotelLocation = getHotelFullLocation(component.component_data, component.location);

          processed.push({
            ...component,
            id: `${component.id}-checkin`,
            originalId: component.id,
            title: `Check-in: ${hotelName}`,
            start_datetime: checkInDate,
            isHotelEvent: true,
            eventType: 'checkin',
            hotelData: component.component_data,
            originalComponent: component,
            tripId,
            tripTitle,
            isConfidential
          });

          processed.push({
            ...component,
            id: `${component.id}-checkout`,
            originalId: component.id,
            title: `Check-out: ${hotelName}`,
            start_datetime: checkOutDate,
            isHotelEvent: true,
            eventType: 'checkout',
            hotelData: component.component_data,
            originalComponent: component,
            tripId,
            tripTitle,
            isConfidential
          });
        }
        // Handle rental car pickup/return
        else if ((component.component_type === 'rental_car' || component.component_type === 'car_rental') &&
            (component.start_datetime || component.component_data?.pickup_datetime || component.component_data?.pickup_date)) {

          const rentalCompany = component.component_data?.rental_company ||
                                component.component_data?.rental_car_company ||
                                component.title ||
                                'Car Rental';
          const vehicleType = component.component_data?.vehicle_type ||
                              component.component_data?.car_type ||
                              '';
          const displayName = vehicleType ? `${rentalCompany} - ${vehicleType}` : rentalCompany;

          // Get pickup datetime - prefer start_datetime (clean, no timezone suffix), then pickup_datetime
          let pickupDateTime = component.start_datetime || component.component_data?.pickup_datetime;
          if (!pickupDateTime && component.component_data?.pickup_date) {
            const pickupDate = component.component_data.pickup_date;
            const pickupTime = component.component_data.pickup_time || '12:00';
            pickupDateTime = pickupDate.includes('T') ? pickupDate : `${pickupDate}T${pickupTime}`;
          }

          // Get dropoff datetime - prefer end_datetime (clean), then dropoff_datetime
          let dropoffDateTime = component.end_datetime || component.component_data?.dropoff_datetime;
          if (!dropoffDateTime && component.component_data?.dropoff_date) {
            const dropoffDate = component.component_data.dropoff_date;
            const dropoffTime = component.component_data.dropoff_time || '12:00';
            dropoffDateTime = dropoffDate.includes('T') ? dropoffDate : `${dropoffDate}T${dropoffTime}`;
          }

          const pickupLocation = component.component_data?.pickup_location || component.location || '';
          const dropoffLocation = component.component_data?.dropoff_location || pickupLocation || '';

          // Pickup card
          if (pickupDateTime) {
            processed.push({
              ...component,
              id: `${component.id}-pickup`,
              originalId: component.id,
              title: `Pickup: ${displayName}`,
              start_datetime: pickupDateTime,
              end_datetime: null,
              location: pickupLocation,
              isRentalCarEvent: true,
              eventType: 'pickup',
              rentalData: component.component_data,
              originalComponent: component,
              tripId,
              tripTitle,
              isConfidential
            });
          }

          // Return card
          if (dropoffDateTime) {
            processed.push({
              ...component,
              id: `${component.id}-return`,
              originalId: component.id,
              title: `Return: ${displayName}`,
              start_datetime: dropoffDateTime,
              end_datetime: null,
              location: dropoffLocation,
              isRentalCarEvent: true,
              eventType: 'return',
              rentalData: component.component_data,
              originalComponent: component,
              tripId,
              tripTitle,
              isConfidential
            });
          }
        }
        // Regular component
        else {
          const location = component.component_type === 'hotel'
            ? getHotelFullLocation(component.component_data, component.location)
            : component.location;

          processed.push({
            ...component,
            originalId: component.id,
            location: location,
            isFlightLeg: false,
            isHotelEvent: false,
            originalComponent: component,
            tripId,
            tripTitle,
            isConfidential
          });
        }
      });
    });

    // Sort by start datetime
    processed.sort((a, b) => {
      const dateA = getComponentStartDatetime(a);
      const dateB = getComponentStartDatetime(b);
      const timeA = parseDatetimeForSortValue(dateA);
      const timeB = parseDatetimeForSortValue(dateB);
      if (timeA === null && timeB === null) return 0;
      if (timeA === null) return 1;
      if (timeB === null) return -1;
      return timeA - timeB;
    });

    return processed;
  }, [trips, selectedExecutiveId, isSpeakerBureau]);

  const allTimelineComponents = buildTimeline();

  // Filter timeline components based on filter selection.
  // Semantics (including per-type grace periods and timezone handling) live
  // in utils/timelineFilters and are shared with the trip timeline.
  const filterTimelineComponents = (components) => {
    const now = new Date();
    return components.filter(component => {
      return passesTimeFilter(component, timelineFilter, {
        now,
        range: timelineDateRange || null,
      });
    });
  };

  const timelineComponents = filterTimelineComponents(allTimelineComponents);

  // Consolidated flights: other travellers' flight legs on the same trips,
  // named after their assigned executives when the booking lists no passengers.
  // Only assistants and admins get the option; executives and speakers see
  // their own flights alone.
  const canConsolidateFlights = !isSpeakerBureau && !isRestrictedUser;
  const companionFlightLegs = (() => {
    if (!consolidateFlights || !canConsolidateFlights) return [];
    const ownIds = new Set(allTimelineComponents.map(c => c.id));
    const nameOf = (id) => {
      const exec = executives.find(e => String(e.id) === String(id));
      return exec ? (exec.full_name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim()) : null;
    };
    return buildTimeline(null)
      .filter(c => c.isFlightLeg && !ownIds.has(c.id) && !c.isConfidential)
      .map(c => ({ ...c, travellerNames: (c.component_data?.assigned_executives || []).map(nameOf).filter(Boolean) }));
  })();

  // Per-trip effective access for the current user.
  // For non-executive users (EAs, admins) this is always 'write' so existing
  // UI behavior is preserved. For an executive viewing their own dashboard,
  // it's derived from trip.executive_permissions.
  const tripAccessByTripId = useMemo(() => {
    const map = {};
    if (!isExecutiveUser || !linkedExecId) {
      (allTrips || []).forEach(t => { if (t?.id) map[t.id] = 'write'; });
      return map;
    }
    (allTrips || []).forEach(t => {
      if (!t?.id) return;
      const perms = Array.isArray(t.executive_permissions) ? t.executive_permissions : [];
      const entry = perms.find(p => p && p.executive_id === linkedExecId);
      map[t.id] = entry?.level === 'write' ? 'write' : entry?.level === 'read' ? 'read' : 'none';
    });
    return map;
  }, [allTrips, isExecutiveUser, linkedExecId]);

  const getTripAccess = (tripId) => {
    // Speaker portal accounts are always read-only on their trips
    if (isSpeakerUser) return 'read';
    return tripAccessByTripId[tripId] || (isExecutiveUser ? 'none' : 'write');
  };

  // Apply filters to timeline components
  const filteredTimelineComponents = timelineComponents.filter(component => {
    const allTripsSelected = selectedTripIds.length === trips.length || selectedTripIds.length === 0;
    const tripMatch = allTripsSelected || selectedTripIds.includes(component.tripId);

    const componentType = component.component_type === 'car_rental' ? 'rental_car' : component.component_type;
    const allTypesSelected = selectedComponentTypes.length === COMPONENT_TYPE_OPTIONS.length;
    const typeMatch = allTypesSelected || selectedComponentTypes.includes(componentType);

    return tripMatch && typeMatch;
  });

  // Generate trip options for filter dropdown
  const tripFilterOptions = trips.map(trip => ({
    value: trip.id,
    label: trip.title || 'Untitled Trip'
  }));

  // Get selected executive
  const selectedExecutive = executives.find(e => e.id === selectedExecutiveId);

  // Whether to render the Travel Timeline section. The timeline subject is an
  // executive (ea_travel) or a searched / "show all" speaker (speaker_bureau).
  // Speaker bureau renders once a speaker is picked (or "show all") - even with
  // zero components, so the inner "No travel components yet" state shows -
  // mirroring how the executive timeline populates from the pulldown selection.
  const showTimelineSection = isSpeakerBureau
    ? (Boolean(selectedSpeaker) || showAllPrograms)
    : (Boolean(selectedExecutive) && allTimelineComponents.length > 0);

  // Trip carousel navigation
  // Executive users only see trips they have at least 'read' access to.
  const accessibleTrips = isExecutiveUser
    ? trips.filter(t => {
        const lvl = tripAccessByTripId[t.id];
        return lvl === 'read' || lvl === 'write';
      })
    : trips;
  const visibleTripsCount = 3;
  const canScrollLeft = tripCarouselIndex > 0;
  const canScrollRight = tripCarouselIndex + visibleTripsCount < accessibleTrips.length;
  const visibleTrips = accessibleTrips.slice(tripCarouselIndex, tripCarouselIndex + visibleTripsCount);

  const handleTripCarouselPrev = () => {
    setTripCarouselIndex(prev => Math.max(0, prev - 1));
  };

  const handleTripCarouselNext = () => {
    setTripCarouselIndex(prev => Math.min(accessibleTrips.length - visibleTripsCount, prev + 1));
  };

  // User preferences
  const prefs = user?.ui_preferences || { date_input_format: 'US', time_view_format: 'AMPM' };

  // Executive timeline in the modern theme's list view: day groups, journey
  // cards and the at-a-glance rail. Grid view and speaker bureaus keep cards.
  const showJourneyLayout = !isSpeakerBureau && layoutMode === 'list';
  const journeyAirportName = (code) => AIRPORT_NAMES[code] || '';

  const renderTimelineCard = (component) => {
    const noteProps = {
      stickyNotes: getNotesForComponent(component.tripId, component.id),
      currentUserId: user?.id,
      onToggleNoteExpand: handleToggleNoteExpand,
      onEditNote: handleEditNote,
      onDeleteNote: handleDeleteNote,
      onNotePositionChange: handleNotePositionChange,
      onNoteMoveToComponent: handleNoteMoveToComponent,
      // Parsing-issue reporting is an EA / meeting-planner tool;
      // executives and speakers don't see the report button
      onReportFeedback: isRestrictedUser ? undefined : handleReportFeedback,
      onOpenConfidentiality: handleOpenConfidentialityModal,
      prefs,
      selectedExecutiveId,
      executives,
      // Speaker portal accounts get the same read-only card
      // treatment as read-access executives
      isExecutiveUser: isRestrictedUser,
      tripAccess: getTripAccess(component.tripId)
    };

    const cardContent = component.isFlightLeg
      ? <FlightCard component={component} {...noteProps} />
      : component.isHotelEvent
        ? <HotelCard component={component} {...noteProps} />
        : <GenericCard component={component} {...noteProps} />;

    return (
      <div
        key={component.id}
        onClick={() => handlePlaceNoteOnCard(component.tripId, component.id)}
        className={isPlacingNote ? 'cursor-pointer ring-2 ring-ui-amber-solid ring-offset-2 ring-offset-ui-page rounded-2xl transition-all duration-200 hover:ring-ui-amber-fg' : ''}
      >
        {cardContent}
      </div>
    );
  };

  // Journey layout: a hotel's check-in is a stay card and its check-out a card
  // built around the departure transfer. Rides are booked on the trip page, so
  // the booking buttons open it with ?bookTransfer.
  const renderHotelStayCard = (component) => {
    const access = getTripAccess(component.tripId);
    const canEdit = !isRestrictedUser || access === 'write';
    const hotelData = component.hotelData || component.component_data || {};
    const card = { ...component, location: getHotelFullLocation(hotelData, component.location) };
    const index = filteredTimelineComponents.findIndex(c => c.id === component.id);
    const ends = index < 0 ? {} : hotelTransferEnds(filteredTimelineComponents, index, (c) =>
      c.tripId === component.tripId && URBANRIDE_ELIGIBLE_TYPES.includes(c.component_type));
    const tripUrl = `/Trip/${component.tripId}`;
    const bookTransfer = canEdit ? () => navigate(`${tripUrl}?bookTransfer=${encodeURIComponent(component.id)}`, { state: tripOriginState(location) }) : undefined;
    const shared = {
      component: card,
      canViewTrip: !isRestrictedUser || access === 'read' || access === 'write',
      onEdit: canEdit ? () => navigate(`${tripUrl}?editComponent=${component.originalId}`, { state: tripOriginState(location) }) : undefined,
      isConfidential: component.isConfidential,
      onOpenConfidentiality: isRestrictedUser ? undefined : () => handleOpenConfidentialityModal({ isUnfiled: false, tripId: component.tripId, componentId: component.originalId, title: hotelData.hotel_name || component.title || 'Hotel' }),
      insideNotes: getNotesForComponent(component.tripId, component.id),
      noteHandlers: { onToggleNoteExpand: handleToggleNoteExpand, onEditNote: handleEditNote, onDeleteNote: handleDeleteNote },
      currentUserId: user?.id,
      prefs,
    };
    return (
      <div
        key={component.id}
        onClick={() => handlePlaceNoteOnCard(component.tripId, component.id)}
        className={isPlacingNote ? 'cursor-pointer ring-2 ring-ui-amber-solid ring-offset-2 ring-offset-ui-page rounded-2xl transition-all duration-200 hover:ring-ui-amber-fg' : ''}
      >
        {component.eventType === 'checkout' ? (
          <HotelCheckoutCard {...shared} departureTransfer={findHotelTransfer(hotelData, 'departure')} nextStop={describeStop(ends.to)} onBookDeparture={bookTransfer} />
        ) : (
          <HotelStayCard
            {...shared}
            trip={trips.find(t => t.id === component.tripId)}
            arrivalTransfer={findHotelTransfer(hotelData, 'arrival')}
            arrivalFrom={ends.from?.title}
            onBookArrival={ends.from ? bookTransfer : undefined}
          />
        )}
      </div>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // EA Inbox section - rendered in the main dashboard AND in the no-executives
  // welcome state, so items parsed from inbound email are visible (and can be
  // filed, which creates the first speaker/program) before any executive or
  // speaker exists in a brand-new organization.
  const renderEaInboxSection = () => {
    if (isRestrictedUser || eaInboxItems.length === 0) return assignmentNotice ? <p role="status">{assignmentNotice}</p> : null;
    return (
      <div className="mb-10">
        {assignmentNotice && <p role="status" className="mb-3">{assignmentNotice}</p>}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-ui-amber-bg">
              <Inbox className="w-5 h-5 text-ui-amber-fg" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-ui-ink">
                Inbox
              </h2>
              <p className={`text-sm ${textClasses.subtitle}`}>{!isSpeakerBureau && selectedExecutiveId ? "Shared inbox and selected executive items requiring assignment" : "Items requiring assignment"}</p>
            </div>
            <span className="px-3 py-1 text-sm font-bold rounded-full bg-ui-amber-bg text-ui-amber-fg">
              {eaInboxItems.length}
            </span>
          </div>
        </div>

        {eaInboxLoading ? (
          <div className={`flex items-center justify-center py-8 ${textClasses.muted}`}>
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {'Loading ' + labels.eaInbox.toLowerCase() + ' items...'}
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {eaInboxItems.slice(0, 8).map((item) => (
                <InboxCard
                  key={item.id}
                  item={item}
                  executives={executives}
                  speakers={speakers}
                  isSpeakerBureau={isSpeakerBureau}
                  onSetup={() => setSetupItem(item)}
                  trips={allTrips}
                  selectedExecutiveId={selectedExecutives[item.id]}
                  selectedTripId={selectedTrips[item.id]}
                  onExecutiveSelect={(id, val) => setSelectedExecutives(prev => ({ ...prev, [id]: val }))}
                  onTripSelect={(id, val) => setSelectedTrips(prev => ({ ...prev, [id]: val }))}
                  onFile={handleFile}
                  onDelete={handleDelete}
                  onOpenConfidentiality={handleOpenConfidentialityModal}
                  isFiling={filingId === item.id}
                  isDeleting={deletingId === item.id}
                  prefs={prefs}
                />
              ))}
            </div>
            {eaInboxItems.length > 8 && (
              <Link to="/unfiled" className="mt-4 block text-center text-sm text-ui-link hover:text-ui-blue-fg">
                View all {eaInboxItems.length} items →
              </Link>
            )}
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 min-h-screen bg-ui-page">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 rounded-full animate-spin border-ui-blue-solid/30 border-t-ui-blue-solid" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Plane className="w-6 h-6 text-ui-blue-solid" />
            </div>
          </div>
          <p className="mt-4 font-medium text-ui-ink-3">Loading your travel hub...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-6 min-h-screen bg-ui-page">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-ui-red-bg">
            <AlertCircle className="w-8 h-8 text-ui-red-fg" />
          </div>
          <h3 className={`text-xl font-semibold mb-2 ${textClasses.title}`}>Something went wrong</h3>
          <p className={`mb-6 ${textClasses.subtitle}`}>{error}</p>
          <Button onClick={fetchExecutives} className={buttonClasses.primary}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // First-run welcome gate. Speaker bureaus skip it entirely: speakers are
  // not set up in advance there - they get created from inbox items (the
  // quick-program wizard) or the header's "+ New" button - so the dashboard
  // renders normally even with no speakers/executives yet.
  if (!isSpeakerBureau && executives.length === 0) {
    const hasInboxItems = !isExecutiveUser && eaInboxItems.length > 0;
    return (
      <div className={`p-6 min-h-screen bg-ui-page ${hasInboxItems ? '' : 'flex items-center justify-center'}`}>
        <div className={hasInboxItems ? 'max-w-6xl mx-auto' : ''}>
          <div className={`text-center max-w-md mx-auto ${hasInboxItems ? 'py-8' : ''}`}>
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-ui-primary to-ui-teal-strong shadow-lg shadow-ui-blue-solid/30">
              <User className="w-10 h-10 text-ui-on-primary" />
            </div>
            <h3 className={`text-2xl font-bold mb-3 ${textClasses.title}`}>Welcome to Veyage</h3>
            <p className={`mb-8 ${textClasses.subtitle}`}>{labels.addAnExecutive} with your premium travel management experience.</p>
            <Link to="/executives">
              <Button className={buttonClasses.primary + ' px-6 py-3 rounded-xl font-semibold'}>
                <Plus className="w-5 h-5 mr-2" />
                {labels.addExecutive}
              </Button>
            </Link>
          </div>
          {/* Parsed inbound emails must stay reachable before the first
              speaker/executive exists - filing an item from here is also the
              quickest way to create one (quick-program wizard prefills from
              the parsed data). */}
          {hasInboxItems && (
            <div className="mt-10">
              {renderEaInboxSection()}
            </div>
          )}
        </div>

        {/* Modals needed for inbox interactions in this pre-setup state */}
        {setupItem && (
          <CreateSpeakerProgramModal
            prefill={buildSpeakerProgramPrefill(setupItem)}
            onClose={() => setSetupItem(null)}
            onCreated={handleProgramCreated}
          />
        )}
        {showConfidentialityModal && confidentialityTarget && (
          <ConfidentialitySettingsModal
            isOpen={showConfidentialityModal}
            onClose={() => {
              setShowConfidentialityModal(false);
              setConfidentialityTarget(null);
            }}
            target={confidentialityTarget}
            onUpdate={handleConfidentialityUpdate}
          />
        )}
      </div>
    );
  }

  return (
    <div className={containerClasses.page}>
      {/* Header */}
      {!isSpeakerBureau ? (
        // Executive header: who the schedule belongs to, quick links to their
        // trips, and the page actions.
        <div className="sticky top-0 z-30 bg-ui-page/95 backdrop-blur border-b border-ui-divider">
          {selectedExecutive && (
            <div className="max-w-[1360px] mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3.5 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-ui-indigo-strong flex items-center justify-center flex-none text-lg font-bold text-ui-on-primary" aria-hidden="true">
                  {(selectedExecutive.full_name || selectedExecutive.first_name || 'E')[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <label htmlFor="exec-dashboard-executive" className="block text-xs text-ui-muted mb-0.5">Viewing schedule for</label>
                  <div className="relative">
                    <select
                      id="exec-dashboard-executive"
                      value={selectedExecutiveId || ''}
                      onChange={handleExecutiveChange}
                      disabled={executives.length < 2}
                      className="appearance-none max-w-full bg-transparent border-0 p-0 pr-6 text-lg sm:text-xl font-semibold text-ui-ink truncate focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-blue-solid rounded cursor-pointer disabled:cursor-default disabled:opacity-100"
                    >
                      {executives.map((exec) => {
                        const name = exec.full_name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim();
                        return (
                          <option key={exec.id} value={exec.id} className="bg-ui-surface">
                            {exec.company ? `${name} (${exec.company})` : name}
                          </option>
                        );
                      })}
                    </select>
                    {executives.length > 1 && <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted pointer-events-none" />}
                  </div>
                </div>
              </div>
              {accessibleTrips.length > 0 && (
                <nav aria-label="Trips" className="order-last basis-full xl:order-none xl:basis-auto flex-1 min-w-0 flex items-center gap-2">
                  {accessibleTrips.length > visibleTripsCount && (
                    <button type="button" onClick={handleTripCarouselPrev} disabled={!canScrollLeft} aria-label="Previous trips" className="w-9 h-9 flex-none inline-flex items-center justify-center rounded-lg border border-ui-control text-ui-ink-2 hover:bg-ui-surface disabled:opacity-40 disabled:cursor-not-allowed">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  )}
                  <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {visibleTrips.map((trip) => (
                      <TripLink key={trip.id} to={`/Trip/${trip.id}`} className="group min-w-0 flex items-center gap-2.5 h-11 px-3 rounded-[10px] bg-ui-surface border border-ui-line hover:border-ui-blue-line transition-colors">
                        <Briefcase className="w-4 h-4 flex-none text-ui-link" aria-hidden="true" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-semibold text-ui-ink truncate">{trip.title || 'Untitled Trip'}</span>
                          {trip.start_date && <span className="block text-[11px] text-ui-muted truncate">{formatShortDate(trip.start_date)}</span>}
                        </span>
                        <ArrowUpRight className="w-3.5 h-3.5 flex-none text-ui-muted group-hover:text-ui-blue-fg" aria-hidden="true" />
                      </TripLink>
                    ))}
                  </div>
                  {accessibleTrips.length > visibleTripsCount && (
                    <button type="button" onClick={handleTripCarouselNext} disabled={!canScrollRight} aria-label="More trips" className="w-9 h-9 flex-none inline-flex items-center justify-center rounded-lg border border-ui-control text-ui-ink-2 hover:bg-ui-surface disabled:opacity-40 disabled:cursor-not-allowed">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </nav>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {!isExecutiveUser && (
                  <Link to={`/executives?id=${selectedExecutiveId}`} className="h-10 px-3.5 inline-flex items-center gap-1.5 rounded-[10px] border border-ui-control text-[13px] text-ui-ink-2 hover:bg-ui-surface transition-colors">
                    Profile
                  </Link>
                )}
                <Link to="/trips" className="h-10 px-3.5 inline-flex items-center gap-1.5 rounded-[10px] border border-ui-control text-[13px] text-ui-ink-2 hover:bg-ui-surface transition-colors">
                  All trips
                </Link>
                {!isExecutiveUser && (
                  <button type="button" onClick={() => setShowCreateTripModal(true)} className="h-10 px-4 inline-flex items-center gap-1.5 rounded-[10px] bg-ui-primary hover:bg-ui-primary-hover text-[13px] font-semibold text-ui-on-primary transition-colors">
                    <Plus className="w-4 h-4" aria-hidden="true" />New trip
                  </button>
                )}
                <NotificationBell />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={containerClasses.header}>
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-ui-primary/20 via-ui-violet-strong/10 to-ui-page" />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-ui-blue-solid/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-ui-violet-solid/20 rounded-full blur-3xl" />

          <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
            {(isSpeakerBureau || selectedExecutive) && (
              <div className="bg-ui-surface rounded-2xl border border-ui-line p-3 sm:p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {isSpeakerBureau ? (
                  isSpeakerUser ? (
                  /* Speaker portal account: own profile, no search / management links */
                  <div className="flex items-center gap-3 lg:min-w-[280px]">
                    <div className="w-10 h-10 bg-gradient-to-br from-ui-red-strong to-ui-pink-strong rounded-xl flex items-center justify-center shadow-lg shadow-ui-pink-solid/30 flex-shrink-0">
                      <span className="text-lg font-bold text-ui-on-primary">
                        {(selectedSpeaker?.full_name || selectedSpeaker?.first_name || 'S')[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-bold text-ui-ink truncate">
                        {selectedSpeaker?.full_name || `${selectedSpeaker?.first_name || ''} ${selectedSpeaker?.last_name || ''}`.trim() || 'My Engagements'}
                      </p>
                      <p className="text-xs text-ui-ink-3">My speaking engagements</p>
                    </div>
                  </div>
                  ) : (
                  /* Speaker search selector (scales to hundreds of speakers) */
                  <div className="flex items-center gap-3 flex-1 min-w-0 lg:min-w-[440px]">
                    <div className="w-10 h-10 bg-gradient-to-br from-ui-red-strong to-ui-pink-strong rounded-xl flex items-center justify-center shadow-lg shadow-ui-pink-solid/30 flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-ui-on-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <SpeakerProfileSelector
                        selectedSpeaker={selectedSpeaker}
                        onSelect={(s) => { setSelectedSpeaker(s); if (s) setShowAllPrograms(false); }}
                        showAll={showAllPrograms}
                        onToggleShowAll={(next) => { setShowAllPrograms(next); if (next) setSelectedSpeaker(null); }}
                      />
                    </div>
                    <div className="hidden sm:flex items-center gap-2">
                      <Link to="/executives" className="text-xs text-ui-link hover:text-ui-blue-fg flex items-center gap-1 transition-colors">
                        <User className="w-3.5 h-3.5" />Speakers
                      </Link>
                      <span className="text-ui-faint">•</span>
                      <Link to="/trips" className="text-xs text-ui-link hover:text-ui-blue-fg flex items-center gap-1 transition-colors">
                        <Briefcase className="w-3.5 h-3.5" />Trips
                      </Link>
                      <span className="text-ui-faint">•</span>
                      <button onClick={() => setShowNewProgram(true)} className="text-xs text-ui-green-fg hover:text-ui-green-fg flex items-center gap-1 transition-colors">
                        <Plus className="w-3.5 h-3.5" />New
                      </button>
                    </div>
                  </div>
                  )
                  ) : (
                  /* Executive selector */
                  <div className="flex items-center gap-3 lg:min-w-[280px]">
                    <div className="w-10 h-10 bg-gradient-to-br from-ui-primary to-ui-violet-strong rounded-xl flex items-center justify-center shadow-lg shadow-ui-blue-solid/30 flex-shrink-0">
                      <span className="text-lg font-bold text-ui-on-primary">
                        {(selectedExecutive?.full_name || selectedExecutive?.first_name || 'E')[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="relative">
                        <select
                          value={selectedExecutiveId || ''}
                          onChange={handleExecutiveChange}
                          className="appearance-none w-full bg-ui-hover backdrop-blur-sm border border-ui-line rounded-lg px-3 py-1.5 pr-8 text-base font-bold text-ui-ink focus:outline-none focus:ring-2 focus:ring-ui-primary/50 focus:border-ui-primary/50 cursor-pointer truncate"
                        >
                          {executives.map((exec) => {
                            const name = exec.full_name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim();
                            return (
                              <option key={exec.id} value={exec.id}>
                                {exec.company ? `${name} (${exec.company})` : name}
                              </option>
                            );
                          })}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-ink-3 pointer-events-none" />
                      </div>
                    </div>
                    {/* Quick links - hide management links for executive users */}
                    <div className="hidden sm:flex items-center gap-2">
                      {!isExecutiveUser && (
                        <>
                          <Link to={`/executives?id=${selectedExecutiveId}`} className="text-xs text-ui-link hover:text-ui-blue-fg flex items-center gap-1 transition-colors">
                            <User className="w-3.5 h-3.5" />Profile
                          </Link>
                          <span className="text-ui-faint">•</span>
                        </>
                      )}
                      <Link to="/trips" className="text-xs text-ui-link hover:text-ui-blue-fg flex items-center gap-1 transition-colors">
                        <Briefcase className="w-3.5 h-3.5" />Trips
                      </Link>
                      {!isExecutiveUser && (
                        <>
                          <span className="text-ui-faint">•</span>
                          <button onClick={() => setShowCreateTripModal(true)} className="text-xs text-ui-green-fg hover:text-ui-green-fg flex items-center gap-1 transition-colors">
                            <Plus className="w-3.5 h-3.5" />New
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  )}

                  {/* Trip cards carousel - only in modern */}
                  <div className="hidden lg:block w-px bg-ui-divider self-stretch" />
                  <div className="flex-1 min-w-0">
                    {tripsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 text-ui-blue-solid animate-spin" />
                      </div>
                    ) : accessibleTrips.length === 0 ? (
                      <div className="flex items-center justify-center py-2 text-center gap-3">
                        <p className="text-ui-ink-3 text-sm">{isRestrictedUser ? 'No trips shared with you yet' : 'No trips yet'}</p>
                        {!isRestrictedUser && (
                          <Button size="sm" onClick={() => setShowCreateTripModal(true)} className="bg-gradient-to-r from-ui-primary to-ui-teal-strong hover:opacity-90 text-ui-on-primary rounded-lg text-xs py-1 px-2">
                            <Plus className="w-3 h-3 mr-1" />Create
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {accessibleTrips.length > visibleTripsCount && (
                          <button onClick={handleTripCarouselPrev} disabled={!canScrollLeft} className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-200 ${canScrollLeft ? 'bg-ui-hover text-ui-ink hover:bg-ui-line border border-ui-divider' : 'bg-ui-hover text-ui-faint cursor-not-allowed border border-ui-divider'}`}>
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                        )}
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {visibleTrips.map((trip) => (
                            <TripLink key={trip.id} to={`/Trip/${trip.id}`} className="group relative bg-ui-hover backdrop-blur-sm rounded-lg border border-ui-divider p-2.5 hover:border-ui-blue-line transition-all duration-300">
                              <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-gradient-to-br from-ui-primary to-ui-teal-strong rounded-md shadow-sm">
                                  <Briefcase className="w-3 h-3 text-ui-on-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-xs font-semibold text-ui-on-primary truncate">{trip.title || 'Untitled Trip'}</h3>
                                  {trip.start_date && <p className="text-[10px] text-ui-ink-3 truncate">{formatShortDate(trip.start_date)}</p>}
                                </div>
                                <ArrowUpRight className="w-3 h-3 text-ui-muted group-hover:text-ui-link flex-shrink-0" />
                              </div>
                            </TripLink>
                          ))}
                        </div>
                        {accessibleTrips.length > visibleTripsCount && (
                          <button onClick={handleTripCarouselNext} disabled={!canScrollRight} className={`flex-shrink-0 p-1.5 rounded-lg transition-all duration-200 ${canScrollRight ? 'bg-ui-hover text-ui-ink hover:bg-ui-line border border-ui-divider' : 'bg-ui-hover text-ui-faint cursor-not-allowed border border-ui-divider'}`}>
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end lg:justify-center flex-shrink-0">
                    <NotificationBell />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={isSpeakerBureau ? 'px-4 sm:px-6 lg:px-8 py-6 sm:py-8' : 'max-w-[1360px] mx-auto px-4 sm:px-6 pt-6 pb-16'}>
        {/* EA Inbox Section - hidden for executive users */}
        {renderEaInboxSection()}

        {/* Travel Timeline Section */}
        {showTimelineSection && (
          <div className={showJourneyLayout ? 'grid gap-6 items-start lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_360px]' : ''}>
          <div className="min-w-0">
            <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${showJourneyLayout ? 'mb-4' : 'mb-6'}`}>
              <div className="flex items-center gap-3">
                {isSpeakerBureau && (
                  <div className="p-2.5 rounded-xl bg-ui-green-bg">
                    <Calendar className="w-5 h-5 text-ui-green-fg" />
                  </div>
                )}
                <h2 className="text-xl font-bold text-ui-ink">Travel timeline</h2>
              </div>

              {/* Filters and Controls */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Trip / Component filters — hidden for speaker portal accounts,
                    whose small timeline doesn't need narrowing */}
                {!isSpeakerUser && trips.length > 0 && (
                  <MultiSelectDropdown
                    label="Trips"
                    options={tripFilterOptions}
                    selectedValues={selectedTripIds}
                    onChange={setSelectedTripIds}
                    icon={Briefcase}
                    allSelectedLabel="All Trips"
                  />
                )}

                {!isSpeakerUser && trips.length > 0 && (
                  <MultiSelectDropdown
                    label="Components"
                    options={COMPONENT_TYPE_OPTIONS}
                    selectedValues={selectedComponentTypes}
                    onChange={setSelectedComponentTypes}
                    icon={Calendar}
                    allSelectedLabel="All Components"
                  />
                )}

                {/* Time Filter */}
                <div className="flex items-center gap-1 p-1 bg-ui-surface rounded-[10px] border border-ui-control">
                  {['today', 'tomorrow', 'upcoming', 'all'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => { setTimelineFilter(filter); setTimelineVisibleCount(15); }}
                      className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                        timelineFilter === filter
                          ? 'bg-ui-blue-line text-ui-ink'
                          : 'text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover'
                      }`}
                      title={`Show ${filter} items`}
                    >
                      {filter === 'tomorrow' ? (
                        <>
                          <span className="hidden sm:inline">Tomorrow</span>
                          <span className="sm:hidden">Tmrw</span>
                        </>
                      ) : (
                        filter.charAt(0).toUpperCase() + filter.slice(1)
                      )}
                    </button>
                  ))}

                  {/* Date Range Filter */}
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <button
                        onClick={() => setTimelineFilter('range')}
                        className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                          timelineFilter === 'range'
                            ? 'bg-ui-blue-line text-ui-ink'
                            : 'text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover'
                        }`}
                        title="Filter by custom date range"
                      >
                        <Calendar className="w-3.5 h-3.5" />
                        {timelineFilter === 'range' && (timelineDateRange?.from || timelineDateRange?.to) ? (
                          <span className="hidden sm:inline">
                            {timelineDateRange.from ? formatDate(timelineDateRange.from, prefs) : '…'}
                            {' – '}
                            {timelineDateRange.to ? formatDate(timelineDateRange.to, prefs) : '…'}
                          </span>
                        ) : (
                          <span className="hidden sm:inline">Range</span>
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-ui-raised border-ui-line text-ui-ink shadow-ui-pop" align="end">
                      <div className="p-3 border-b border-ui-divider flex items-center justify-between gap-3">
                        <div className="text-xs font-medium text-ui-ink-2">
                          {timelineDateRange?.from || timelineDateRange?.to ? (
                            <>
                              <span>{timelineDateRange.from ? formatDate(timelineDateRange.from, prefs) : 'Start date'}</span>
                              <span className="mx-1 text-ui-muted">→</span>
                              <span>{timelineDateRange.to ? formatDate(timelineDateRange.to, prefs) : 'End date'}</span>
                            </>
                          ) : (
                            <span className="text-ui-muted">Pick a start and end date</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setTimelineDateRange({ from: undefined, to: undefined });
                            setTimelineFilter('upcoming');
                            setTimelineVisibleCount(15);
                            setDatePickerOpen(false);
                          }}
                          className="text-xs text-ui-muted hover:text-ui-ink-2 underline"
                        >
                          Clear
                        </button>
                      </div>
                      <CalendarUI
                        mode="range"
                        numberOfMonths={2}
                        selected={timelineDateRange}
                        defaultMonth={timelineDateRange?.from || new Date()}
                        onSelect={(range) => {
                          setTimelineDateRange(range || { from: undefined, to: undefined });
                          setTimelineFilter('range');
                          setTimelineVisibleCount(15);
                          if (range?.from && range?.to) {
                            setDatePickerOpen(false);
                          }
                        }}
                        captionLayout="dropdown-buttons"
                        fromYear={new Date().getFullYear() - 5}
                        toYear={new Date().getFullYear() + 5}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Sticky Note Button (hidden for view-only speaker accounts) */}
                {!isSpeakerUser && (
                <button
                  onClick={handleCreateNote}
                  className={`p-2 rounded-lg transition-all duration-200 border ${
                    isPlacingNote
                      ? 'bg-ui-amber-bg text-ui-amber-fg ring-2 ring-ui-amber-solid border-ui-amber-line'
                      : 'text-ui-amber-fg hover:bg-ui-amber-bg bg-ui-surface border-ui-control'
                  }`}
                  title={isPlacingNote ? 'Click on a card to place the note' : 'Add a sticky note'}
                >
                  <StickyNoteIcon className="w-4 h-4" />
                </button>
                )}

                {showJourneyLayout && canConsolidateFlights && (
                  <button
                    type="button"
                    onClick={toggleConsolidateFlights}
                    aria-pressed={consolidateFlights}
                    aria-label="Combine shared flights"
                    title="Show travellers on the same flights in one card"
                    className={`h-[42px] px-3 inline-flex items-center gap-1.5 rounded-[10px] border text-[13px] transition-colors ${
                      consolidateFlights
                        ? 'bg-ui-blue-line border-ui-blue-line text-ui-ink'
                        : 'bg-ui-surface border-ui-control text-ui-ink-2 hover:bg-ui-hover'
                    }`}
                  >
                    <Users className="w-4 h-4" aria-hidden="true" />
                    <span className="hidden sm:inline">Combine shared flights</span>
                  </button>
                )}

                {/* Layout Toggle */}
                <div className="flex items-center gap-1 p-1 bg-ui-surface rounded-[10px] border border-ui-control">
                  <button
                    onClick={() => handleLayoutChange('list')}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      layoutMode === 'list'
                        ? 'bg-ui-blue-line text-ui-ink'
                        : 'text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover'
                    }`}
                    title="List view"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleLayoutChange('grid')}
                    className={`p-2 rounded-lg transition-all duration-200 ${
                      layoutMode === 'grid'
                        ? 'bg-ui-blue-line text-ui-ink'
                        : 'text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover'
                    }`}
                    title="Grid view"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Timeline Content */}
            {tripsLoading ? (
              <div className={`flex items-center justify-center py-8 ${textClasses.muted}`}>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading timeline...
              </div>
            ) : (isSpeakerBureau && !selectedSpeaker && !showAllPrograms) ? (
              <div className="rounded-xl p-8 text-center bg-ui-surface border border-ui-line">
                <Search className="w-12 h-12 mx-auto mb-3 text-ui-faint" />
                <p className={textClasses.subtitle}>Search for a speaker above to see their programs.</p>
                <button
                  onClick={() => setShowAllPrograms(true)}
                  className="mt-3 text-sm font-semibold text-ui-pink-fg hover:text-ui-pink-solid"
                >
                  Or show all programs →
                </button>
              </div>
            ) : timelineComponents.length === 0 ? (
              <div className="rounded-xl p-8 text-center bg-ui-surface border border-ui-line">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-ui-faint" />
                <p className={textClasses.subtitle}>No travel components yet</p>
              </div>
            ) : filteredTimelineComponents.length === 0 ? (
              <div className="rounded-xl p-8 text-center bg-ui-surface border border-ui-line">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-ui-faint" />
                <p className={textClasses.subtitle}>
                  {timelineFilter === 'today' ? 'No items scheduled for today' :
                   timelineFilter === 'tomorrow' ? 'No items scheduled for tomorrow' :
                   timelineFilter === 'upcoming' ? 'No upcoming items' :
                   'No components match the current filters'}
                </p>
                <button
                  onClick={() => {
                    setSelectedTripIds([]);
                    setSelectedComponentTypes(COMPONENT_TYPE_OPTIONS.map(opt => opt.value));
                    setTimelineFilter('all');
                    setTimelineVisibleCount(15);
                  }}
                  className="mt-3 text-sm underline text-ui-link hover:text-ui-blue-fg"
                >
                  Show all {allTimelineComponents.length} items
                </button>
              </div>
            ) : (
              <>
                {showJourneyLayout ? (
                  // Day-grouped itinerary; a flight booking's legs share one card
                  <div className="flex flex-col gap-4">
                    {(consolidateFlights && canConsolidateFlights
                      ? consolidateJourneys(groupTimelineByDay(filteredTimelineComponents.slice(0, timelineVisibleCount)), companionFlightLegs)
                      : groupTimelineByDay(filteredTimelineComponents.slice(0, timelineVisibleCount))
                    ).map((day) => (
                      <section key={day.date || 'undated'} className="flex flex-col gap-4" aria-label={day.date || 'Date to be confirmed'}>
                        <DayHeader date={day.date} tripTitles={day.tripTitles} prefs={prefs} />
                        {day.entries.map((entry) => {
                          if (entry.kind === 'item') return entry.component.isHotelEvent ? renderHotelStayCard(entry.component) : renderTimelineCard(entry.component);
                          const first = entry.legs[0];
                          const access = getTripAccess(first.tripId);
                          return (
                            <div
                              key={entry.key}
                              onClick={() => handlePlaceNoteOnCard(first.tripId, first.id)}
                              className={isPlacingNote ? 'cursor-pointer ring-2 ring-ui-amber-solid ring-offset-2 ring-offset-ui-page rounded-2xl transition-all duration-200 hover:ring-ui-amber-fg' : ''}
                            >
                              <JourneyCard
                                legs={entry.legs}
                                companions={entry.companions}
                                prefs={prefs}
                                airportName={journeyAirportName}
                                stickyNotes={entry.legs.flatMap(leg => getNotesForComponent(leg.tripId, leg.id))}
                                currentUserId={user?.id}
                                noteHandlers={{
                                  onToggleNoteExpand: handleToggleNoteExpand,
                                  onEditNote: handleEditNote,
                                  onDeleteNote: handleDeleteNote,
                                  onNotePositionChange: handleNotePositionChange,
                                  onNoteMoveToComponent: handleNoteMoveToComponent,
                                }}
                                onReportFeedback={isRestrictedUser ? undefined : handleReportFeedback}
                                onOpenConfidentiality={handleOpenConfidentialityModal}
                                canViewTrip={!isRestrictedUser || access === 'read' || access === 'write'}
                                canEditTrip={!isRestrictedUser || access === 'write'}
                              />
                            </div>
                          );
                        })}
                      </section>
                    ))}
                  </div>
                ) : (
                  <div className={`grid gap-3 sm:gap-4 ${layoutMode === 'list' ? 'grid-cols-1 max-w-4xl' : 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5'}`}>
                    {filteredTimelineComponents.slice(0, timelineVisibleCount).map(renderTimelineCard)}
                  </div>
                )}
                {/* Load More button for timeline */}
                {filteredTimelineComponents.length > timelineVisibleCount && (
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={() => setTimelineVisibleCount(prev => prev + 15)}
                      className="px-6 py-2 rounded-lg transition-colors text-sm font-medium bg-ui-surface border border-ui-control hover:bg-ui-hover text-ui-ink-2"
                    >
                      Load More ({filteredTimelineComponents.length - timelineVisibleCount} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          {showJourneyLayout && (
            <AtAGlanceRail
              components={allTimelineComponents}
              trips={accessibleTrips}
              prefs={prefs}
              airportName={journeyAirportName}
              canViewTrip={(tripId) => !isRestrictedUser || ['read', 'write'].includes(getTripAccess(tripId))}
            />
          )}
          </div>
        )}
      </div>

      {/* Modals */}
      {noteModalOpen && (
        <StickyNoteModal
          isOpen={noteModalOpen}
          onClose={() => {
            setNoteModalOpen(false);
            setEditingNote(null);
            setNoteTargetTripId(null);
            setNoteTargetComponentId(null);
            setPendingNotePosition(null);
          }}
          onSave={handleSaveNote}
          note={editingNote}
          tripId={noteTargetTripId}
        />
      )}

      {feedbackComponent && (
        <ParsingFeedbackModal
          isOpen={!!feedbackComponent}
          onClose={() => setFeedbackComponent(null)}
          componentId={feedbackComponent?.originalId || feedbackComponent?.id}
          componentType={feedbackComponent?.component_type}
          tripId={feedbackComponent?.tripId}
        />
      )}

      {setupItem && (
        <CreateSpeakerProgramModal
          prefill={buildSpeakerProgramPrefill(setupItem)}
          onClose={() => setSetupItem(null)}
          onCreated={handleProgramCreated}
        />
      )}

      {showNewProgram && (
        <CreateSpeakerProgramModal
          createMeetingComponent
          onClose={() => setShowNewProgram(false)}
          onCreated={(data) => {
            setShowNewProgram(false);
            fetchSpeakers();
            if (data?.speaker) setSelectedSpeaker(data.speaker);
            if (data?.trip?.id) navigate(`/Trip/${data.trip.id}`, { state: tripOriginState(location) });
          }}
        />
      )}

      {showCreateTripModal && (
        <CreateTripModal2
          isOpen={showCreateTripModal}
          onClose={() => setShowCreateTripModal(false)}
          onTripCreated={(newTrip) => {
            setShowCreateTripModal(false);
            navigate(`/Trip/${newTrip.id}`, { state: tripOriginState(location) });
          }}
          executives={executives}
          defaultExecutiveId={selectedExecutiveId}
        />
      )}

      {showEditTripModal && editingTrip && (
        <EditTripModal2
          isOpen={showEditTripModal}
          onClose={() => {
            setShowEditTripModal(false);
            setEditingTrip(null);
          }}
          trip={editingTrip}
          onTripUpdated={() => {
            setShowEditTripModal(false);
            setEditingTrip(null);
            fetchTrips(selectedExecutiveId);
          }}
          executives={executives}
        />
      )}

      {showConfidentialityModal && confidentialityTarget && (
        <ConfidentialitySettingsModal
          isOpen={showConfidentialityModal}
          onClose={() => {
            setShowConfidentialityModal(false);
            setConfidentialityTarget(null);
          }}
          target={confidentialityTarget}
          onUpdate={handleConfidentialityUpdate}
        />
      )}
    </div>
  );
};

export default ExecDashboardV2;
